import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaUser,
  FaCar,
  FaPhone,
  FaUserTie,
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaWallet,
  FaExclamationTriangle,
  FaCheckCircle,
  FaLock,
  FaClock,
  FaRoute,
  FaStar,
  FaTimes,
  FaEye,
  FaBuilding,
  FaBolt,
  FaInfoCircle,
  FaMapMarkerAlt,
} from "react-icons/fa";
import socketService from "../../utils/socketService";
import StatusConfirmationModal from "../../components/shared/StatusConfirmationModal";
import Pagination from "../../components/shared/Pagination";
import {
  getPaymentMethodDisplay,
  getPaymentStatusDisplay,
  getPaymentMethodBadgeClass,
} from "../../utils/paymentDisplayUtils";
import { formatDateDDMMYYYY } from "../../utils/dateUtils";
import { useLocation, Link } from "react-router-dom";
import { autoAssignDriver } from "../../features/admin/adminSlice";
import { API_URL } from "../../config/apiConfig";
 
const getBookingAmount = (booking) => {
  return booking.totalAmount ||
         booking.fare ||
         booking.amount ||
         booking.finalAmount ||
         0;
};

// Driver Row Component with Wallet Balance Display
const DriverRow = ({
  driver,
  selectedBooking,
  assignDriver,
  user,
  API_URL,
}) => {
  const [walletInfo, setWalletInfo] = useState(null);
  const [loadingWallet, setLoadingWallet] = useState(true);

  useEffect(() => {
    const fetchWalletInfo = async () => {
      if (
        !selectedBooking?.paymentMethod ||
        selectedBooking.paymentMethod !== "ONLINE"
      ) {
        setLoadingWallet(false);
        return;
      }

      try {
        let config = {};
        if (user && user.token) {
          config = {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          };
        }

        const driverId = driver._id || driver.id;

        // Get wallet and commission info
        const [walletResponse, commissionResponse] = await Promise.all([
          axios.get(`${API_URL}/wallet/driver/${driverId}`, config),
          axios.get(`${API_URL}/commission/rate?driverId=${driverId}`, config),
        ]);

        if (walletResponse.data.success && commissionResponse.data.success) {
          const walletData = walletResponse.data.data;
          // Use driver.walletBalance as the source of truth (always)
          const walletBalance = walletData.balance || 0;

          const commissionRate =
            commissionResponse.data.data.commissionRate || 10;
          const requiredCommission = Math.round(
            (getBookingAmount(selectedBooking) * commissionRate) / 100,
          );
          const driverEarnings =
            getBookingAmount(selectedBooking) - requiredCommission;
          // For online payments: Driver receives 90%, pending commission auto-deducted if negative balance
          const pendingCommission = walletBalance < 0 ? Math.abs(walletBalance) : 0;
          const effectiveEarnings = Math.max(0, driverEarnings - pendingCommission);
          const newBalance = walletBalance + driverEarnings;

          // For ONLINE payments: Driver always eligible (company receives money, driver gets 90%)
          // No upfront commission required for online payments
          const balanceAfterCommission = walletBalance + driverEarnings; // Driver will receive 90%
          const canAssign = true; // Online payments never block drivers

          setWalletInfo({
            walletBalance,
            commissionRate,
            requiredCommission,
            driverEarnings,
            balanceAfterCommission,
            canAssign,
          });
        }
      } catch (error) {
        console.error("Error fetching wallet info:", error);
      } finally {
        setLoadingWallet(false);
      }
    };

    fetchWalletInfo();
  }, [driver, selectedBooking, user, API_URL]);

  const formatCurrency = (amount) => {
    const numAmount = Number(amount);
    if (isNaN(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const getWalletStatusColor = () => {
    if (!walletInfo) return "text-gray-500";
    if (walletInfo.newBalance >= 1000) return "text-green-600";
    if (walletInfo.newBalance >= 0) return "text-yellow-600";
    if (walletInfo.newBalance >= -1000) return "text-orange-600";
    return "text-red-600";
  };

  const getWalletStatusIcon = () => {
    if (!walletInfo) return <FaWallet className="w-4 h-4 text-gray-400" />;
    if (walletInfo.canAssign)
      return <FaCheckCircle className="w-4 h-4 text-green-500" />;
    return <FaExclamationTriangle className="w-4 h-4 text-red-500" />;
  };

  return (
    <tr key={driver.id || driver._id}>
      <td className="py-3 px-3">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center mr-3">
            <FaUserTie className="text-gray-600 w-4 h-4" />
          </div>
          <div>
            <div className="font-medium text-gray-900">{driver.name} {driver.phone}</div>
            <div className="text-sm text-gray-500">{driver.email}</div>
          </div>
        </div>
      </td>
      <td className="py-3 px-3">
        <div className="text-sm text-gray-900">Available</div>
        {/* For Online Payments: Show simple commission info without wallet balance */}
        {selectedBooking?.paymentMethod === "ONLINE" && (
          <div className="mt-1">
            {loadingWallet ? (
              <div className="flex items-center text-xs text-gray-400">
                <div className="animate-spin w-3 h-3 border border-gray-300 rounded-full border-t-blue-500 mr-1"></div>
                Loading...
              </div>
            ) : walletInfo ? (
              <div className="text-xs text-green-600">
                <div className="flex items-center">
                  <FaCheckCircle className="w-3 h-3 mr-1" />
                  <span className="font-medium">Online Payment</span>
                </div>
                <div className="text-gray-500 mt-1">
                  <div>Driver gets: {formatCurrency(walletInfo.driverEarnings)} (90%)</div>
                  <div>Commission: {formatCurrency(walletInfo.requiredCommission)} (10%)</div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-green-600">
                <FaCheckCircle className="w-3 h-3 mr-1 inline" />
                Online Payment Ready
              </div>
            )}
          </div>
        )}
      </td>
      <td className="py-3 px-3">
        <div className="text-sm">
          <div className="text-gray-900 font-medium">
            {driver.carType || driver.vehicleType || "Standard"}
          </div>
        </div>
      </td>
      <td className="py-3 px-3">
        <div className="flex flex-col space-y-1">
          <button
            onClick={() => assignDriver(driver.id)}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${
              selectedBooking?.paymentMethod === "ONLINE" &&
              walletInfo &&
              !walletInfo.canAssign
                ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
            title={
              selectedBooking?.paymentMethod === "ONLINE" &&
              walletInfo &&
              !walletInfo.canAssign
                ? "Warning: Low wallet balance - click to proceed anyway"
                : "Assign this driver"
            }
          >
            {selectedBooking?.paymentMethod === "ONLINE" &&
            walletInfo &&
            !walletInfo.canAssign ? (
              <span className="flex items-center">
                <FaExclamationTriangle className="w-3 h-3 mr-1" />
                Assign
              </span>
            ) : (
              "Assign"
            )}
          </button>
          {selectedBooking?.paymentMethod === "ONLINE" && walletInfo && (
            <div className="text-xs text-center text-gray-500">
              Commission: {formatCurrency(walletInfo.requiredCommission)}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

// Wallet Status Component for displaying wallet balance in driver list
const WalletStatus = ({ driverId, bookingAmount, user, API_URL, paymentMethod, cachedWalletData }) => {
  const walletInfo = cachedWalletData ? {
    walletBalance: cachedWalletData.walletBalance,
    commissionRate: cachedWalletData.commissionRate,
    requiredCommission: Math.round((bookingAmount * cachedWalletData.commissionRate) / 100),
    driverEarnings: bookingAmount - Math.round((bookingAmount * cachedWalletData.commissionRate) / 100),
    balanceAfterCommission: cachedWalletData.walletBalance + (bookingAmount - Math.round((bookingAmount * cachedWalletData.commissionRate) / 100)),
    canAssign: true,
  } : null;

  const formatCurrency = (amount) => {
    const numAmount = Number(amount);
    if (isNaN(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  if (!walletInfo) {
    return (
      <div className="flex items-center text-xs text-gray-400 mt-1">
        <FaWallet className="w-3 h-3 mr-1" />
        Wallet: --
      </div>
    );
  }

  const getStatusColor = () => {
    // For online payments, always show positive status since drivers are never blocked
    if (walletInfo.canAssign) {
      return walletInfo.balanceAfterCommission >= 1000 ? "text-green-600" : "text-blue-600";
    }
    return "text-red-600"; // This should never happen for online payments
  };

  const getStatusIcon = () => {
    // For online payments, always show success icon since drivers are never blocked
    if (walletInfo.canAssign)
      return <FaCheckCircle className="w-3 h-3 text-green-500" />;
    return <FaLock className="w-3 h-3 text-red-500" />; // This should never happen for online payments
  };

  // For online payments, don't show wallet balance - just show commission info
  if (paymentMethod === "ONLINE") {
    return (
      <div className="mt-1">
        <div className="text-xs text-green-600">
          <div className="flex items-center">
            <FaCheckCircle className="w-3 h-3 mr-1" />
            <span className="font-medium">Online Payment</span>
          </div>
          <div className="text-gray-500 mt-1">
            <div>Driver gets: {formatCurrency(walletInfo.driverEarnings)} (90%)</div>
            <div>Commission: {formatCurrency(walletInfo.requiredCommission)} (10%)</div>
          </div>
        </div>
      </div>
    );
  }

  // For COD payments, show wallet balance as before
  return (
    <div className="mt-1">
      <div className={`flex items-center text-xs ${
        walletInfo.walletBalance < 0 ? 'text-red-600' : 'text-green-600'
      }`}>
        <FaWallet className="w-3 h-3 mr-1" />
        <span className="font-medium">
          Balance: {formatCurrency(walletInfo.walletBalance)}
        </span>
      </div>
    </div>
  );
};

// Assign Button Component with wallet validation
const AssignButton = ({
  driver,
  selectedBooking,
  assignDriver,
  user,
  API_URL,
  cachedWalletData,
}) => {
  const getWalletStatus = () => {
    if (selectedBooking?.paymentMethod !== "ONLINE" || !cachedWalletData) {
      return null;
    }

    const bookingAmount = getBookingAmount(selectedBooking);
    const requiredCommission = Math.round((bookingAmount * cachedWalletData.commissionRate) / 100);

    return {
      canAssign: true,
      walletBalance: cachedWalletData.walletBalance,
      balanceAfterCommission: cachedWalletData.walletBalance + (bookingAmount - requiredCommission),
      requiredCommission,
    };
  };

  const walletStatus = getWalletStatus();

  const handleAssign = () => {
    assignDriver(driver.id || driver._id);
  };

  if (selectedBooking?.paymentMethod !== "ONLINE") {
    // For COD bookings, show normal assign button
    return (
      <button
        onClick={handleAssign}
        className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
      >
        Assign
      </button>
    );
  }

  if (!walletStatus) {
    return (
      <button
        onClick={handleAssign}
        className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
      >
        Assign
      </button>
    );
  }

  // Show different button styles based on wallet status
  if (walletStatus.canAssign) {
    return (
      <button
        onClick={handleAssign}
        className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700 transition-colors flex items-center"
        title={`Balance: ₹${walletStatus.walletBalance} | Commission: ₹${walletStatus.requiredCommission} | After: ₹${walletStatus.balanceAfterCommission}`}
      >
        <FaCheckCircle className="w-3 h-3 mr-1" />
        Assign
      </button>
    );
  } else {
    return (
      <button
        disabled
        className="bg-red-400 text-white px-3 py-1 rounded-md text-sm cursor-not-allowed flex items-center"
        title={`Cannot assign: This should not happen for online payments`}
      >
        <FaLock className="w-3 h-3 mr-1" />
        Blocked
      </button>
    );
  }
};

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorCommissionMap, setVendorCommissionMap] = useState({});
  const [vendorWalletMap, setVendorWalletMap] = useState({});
  const [driverWalletMap, setDriverWalletMap] = useState({});
  const [assignmentType, setAssignmentType] = useState("driver"); // "driver" or "vendor"
  const DRIVER_API_URL = `${API_URL}/driver`;
  const { user } = useSelector((state) => state.auth || {});
  const dispatch = useDispatch();
  const [autoAssignLoading, setAutoAssignLoading] = useState({});

  // Currency formatter for this page (INR, no decimals)
  const formatCurrency = (amount) => {
    const num = Number(amount);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number.isFinite(num) ? num : 0);
  };

  // Helper: extract a readable city name from a viaCities entry (may be string or object)
  const getCityDisplayName = (city) => {
    if (!city) return '';
    if (typeof city === 'string') return city;
    return city.name || city.city || city.address || city.label || 'City';
  };

  // Helper: format viaCities array to group hierarchical state/country entries using commas rather than arrows
  // Helper to format return dates cleanly without showing raw ISO timestamps
  const formatReturnDate = (dateVal) => {
    if (!dateVal) return "";
    if (typeof dateVal === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(dateVal.trim())) {
      return dateVal.trim();
    }
    const formatted = formatDateDDMMYYYY(dateVal);
    if (formatted) return formatted;
    try {
      const d = new Date(dateVal);
      if (!isNaN(d.getTime())) {
        return formatDateDDMMYYYY(d);
      }
    } catch (e) {
      // ignore
    }
    return String(dateVal);
  };

  const formatViaCitiesList = (viaCities) => {
    if (!viaCities || viaCities.length === 0) return '';
    
    const names = viaCities.map(city => {
      if (!city) return '';
      if (typeof city === 'string') return city.trim();
      return (city.name || city.city || city.address || city.label || '').trim();
    }).filter(Boolean);

    if (names.length === 0) return '';

    const INDIAN_STATES_AND_COUNTRIES = new Set([
      'uttarakhand', 'uttar pradesh', 'himachal pradesh', 'punjab', 'haryana', 
      'rajasthan', 'gujarat', 'maharashtra', 'goa', 'karnataka', 'kerala', 
      'tamil nadu', 'andhra pradesh', 'telangana', 'odisha', 'chhattisgarh', 
      'madhya pradesh', 'jharkhand', 'west bengal', 'bihar', 'assam', 
      'arunachal pradesh', 'manipur', 'meghalaya', 'mizoram', 'nagaland', 
      'sikkim', 'tripura', 'delhi', 'chandigarh', 'puducherry', 
      'jammu & kashmir', 'jammu and kashmir', 'ladakh', 
      'dadra and nagar haveli', 'daman and diu', 'lakshadweep', 
      'andaman and nicobar islands', 'india'
    ]);

    const stops = [];
    for (const name of names) {
      const lowerName = name.toLowerCase();
      if (stops.length > 0 && INDIAN_STATES_AND_COUNTRIES.has(lowerName)) {
        stops[stops.length - 1] = `${stops[stops.length - 1]}, ${name}`;
      } else {
        stops.push(name);
      }
    }

    return stops.join(' → ');
  };


  const [totalBookings, setTotalBookings] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [cabTypes, setCabTypes] = useState({});
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [routeDetailModalBooking, setRouteDetailModalBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  // Read URL query param to allow linking from dashboard cards (e.g. /admin/bookings?status=assigned)
  const location = useLocation();

  // When URL query has a status or search param, initialize or update the status filter and search term accordingly
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const statusParam = params.get('status');
      if (statusParam) {
        setStatusFilter(statusParam);
      }
      const searchParam = params.get('search');
      if (searchParam) {
        setSearchTerm(searchParam);
        setStatusFilter('all'); // Clear status filter to show this specific booking
      }
    } catch (e) {
      // ignore
    }
  }, [location.search]);
  const [updatingBookings, setUpdatingBookings] = useState(new Set());
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedRatingData, setSelectedRatingData] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [sortOrder, setSortOrder] = useState("desc");

  // Helper to format time to AM/PM if it's in 24h format
  const formatTimeAMPM = (timeStr) => {
    if (!timeStr) return "";
    if (timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) {
      return timeStr;
    }
    try {
      const [hours, minutes] = timeStr.split(":");
      let h = parseInt(hours);
      if (isNaN(h)) return timeStr;
      const m = minutes ? minutes.substring(0, 2) : "00";
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12;
      h = h ? h : 12;
      return `${h}:${m} ${ampm}`;
    } catch (e) {
      return timeStr;
    }
  };

  // Initialize socket connection
  useEffect(() => {
    // Connect to socket server
    const socket = socketService.connect();

    let cleanBookingUpdated = () => {};
    let cleanBookingStatusChanged = () => {};
    let cleanRideStarted = () => {};
    let cleanRideCompleted = () => {};
    let cleanDriverAssigned = () => {};
    let cleanRatingSubmitted = () => {};
    let cleanBookingCreated = () => {};

    // Only set up listeners if socket connection is successful
    if (socket) {
      // Join admin room to receive updates
      socketService.joinAdminRoom();

      // Listen for booking updates (when a driver is assigned)
      cleanBookingUpdated = socketService.onBookingUpdated((data) => {
        // Update the local state
        setBookings((prevBookings) => {
          // Find the booking that was updated
          const updatedBookings = prevBookings.map((booking) => {
            if (booking._id === data.booking._id) {
              // Create updated booking with driver information
              return {
                ...booking,
                status: "confirmed",
                driver: {
                  _id: data.driver._id,
                  name: data.driver.name,
                  phone: data.driver.phone,
                  vehicleType: data.driver.vehicleType,
                  vehicleModel: data.driver.vehicleModel,
                  vehicleNumber: data.driver.vehicleNumber,
                },
              };
            }
            return booking;
          });
          return updatedBookings;
        });

        toast.success(
          `Driver ${data.driver.name} assigned to booking successfully`,
        );

        // Also refresh the booking data to ensure consistency
        setTimeout(() => {
          refreshBookings();
        }, 500);
      });

      // Listen for booking status changes (ride started, completed, cancelled)
      cleanBookingStatusChanged = socketService.onBookingStatusChanged((data) => {
        console.log('Booking status changed:', data);
        refreshBookings();
      });

      // Listen for ride started events
      cleanRideStarted = socketService.onRideStarted((data) => {
        console.log('Ride started:', data);
        refreshBookings();
      });

      // Listen for ride completed events  
      cleanRideCompleted = socketService.onRideCompleted((data) => {
        console.log('Ride completed:', data);
        refreshBookings();
      });

      // Listen for driver assigned events
      cleanDriverAssigned = socketService.onDriverAssigned((data) => {
        console.log('Driver assigned:', data);
        refreshBookings();
      });

      // Listen for rating submitted events
      cleanRatingSubmitted = socketService.onRatingSubmitted((data) => {
        console.log('Rating submitted:', data);
        refreshBookings();
      });

      // Listen for new booking created events
      cleanBookingCreated = socketService.onBookingCreated((data) => {
        console.log('New booking created:', data);
        refreshBookings();
      });
    }

    // Clean up on component unmount
    return () => {
      cleanBookingUpdated();
      cleanBookingStatusChanged();
      cleanRideStarted();
      cleanRideCompleted();
      cleanDriverAssigned();
      cleanRatingSubmitted();
      cleanBookingCreated();
    };
  }, []);

  // Listen for global header refresh trigger
  React.useEffect(() => {
    const handleGlobalRefresh = () => {
      refreshBookings();
    };
    window.addEventListener("admin:refresh", handleGlobalRefresh);
    window.addEventListener("adminRefreshBookings", handleGlobalRefresh);
    return () => {
      window.removeEventListener("admin:refresh", handleGlobalRefresh);
      window.removeEventListener("adminRefreshBookings", handleGlobalRefresh);
    };
  }, []);

  // Refresh bookings function
  const refreshBookings = async () => {
    try {
      let config = {};
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
      }

      const response = await axios.get(`${API_URL}/booking/admin/all`, config);
      if (response.data && response.data.success) {
        const formattedBookings = response.data.data.map((booking) => {
          // Use the same formatting logic as the main useEffect
          const buildLocation = (type) => {
            const locObj = type === "from" ? booking.pickupLocation : booking.dropLocation;
            const cityField = type === "from"
              ? booking.pickupCity || booking.fromCity
              : booking.dropCity || booking.toCity;

            const primaryField = type === "from" ? booking.from : booking.to;
            const secondaryField = type === "from" ? booking.pickupAddress : booking.dropAddress;

            if (locObj?.name) {
              return `${locObj.name}${locObj.state ? `, ${locObj.state}` : ""}`;
            }

            if (primaryField && typeof primaryField === "string" && primaryField.length < 50) {
              return primaryField;
            }

            return cityField || secondaryField || "Location not specified";
          };

          const cabTypeName = booking.cabType?.name || booking.cabType || booking.cabName || "Standard Cab";

          return {
            _id: booking._id,
            bookingId: booking.bookingId,
            user: {
              _id: booking.user?._id || null,
              name:
                booking.userName ||
                booking.user?.name ||
                booking.passengerDetails?.name ||
                booking.corporateMemberId?.fullName ||
                "Guest User",
              email:
                booking.userEmail ||
                booking.user?.email ||
                booking.passengerDetails?.email ||
                booking.corporateMemberId?.email ||
                "guest@example.com",
              phone:
                booking.userPhone ||
                booking.user?.phone ||
                booking.passengerDetails?.phone ||
                booking.corporateMemberId?.mobileNumber ||
                "",
            },
            from: buildLocation("from"),
            to: buildLocation("to"),
            date: formatDateDDMMYYYY(new Date(booking.date || booking.pickupDate || booking.travelDate || Date.now())),
            pickupTime: formatTimeAMPM(booking.pickupTime || booking.time || booking.travelTime || ""),
            timestamp: new Date(booking.date || booking.pickupDate || booking.travelDate || Date.now()).getTime(),
            serviceType: (() => {
              if (booking.serviceType && booking.serviceType !== "undefined") {
                return booking.serviceType;
              }
              if (booking.airport || booking.airportCode || booking.transferDirection) {
                return "airport";
              }
              if (booking.packageName || booking.rentalHours || booking.rentalKm) {
                return "rental";
              }
              return "outstation";
            })(),
            amount: booking.totalAmount || booking.fare || booking.amount || 0,
            cabType: cabTypeName,
            driver: booking.driver || null,
            status: booking.status || "pending",
            pickupLocation: booking.pickupLocation,
            dropLocation: booking.dropLocation,
            pickupAddress: booking.pickupAddress || booking.pickupLocation?.address,
            dropAddress: booking.dropAddress || booking.dropLocation?.address,
            pickupAddressDetails: booking.pickupAddressDetails,
            dropAddressDetails: booking.dropAddressDetails,
            packageName: booking.packageName || (booking.package && booking.package.name) || (booking.rentalPackageId && (booking.rentalPackageId.name || booking.rentalPackageId.packageName)) || "",
            rentalHours: booking.rentalHours || booking.hours || (booking.rentalPackageId && booking.rentalPackageId.hours) || 0,
            rentalKm: booking.rentalKm || booking.kilometers || (booking.rentalPackageId && booking.rentalPackageId.kilometers) || 0,
            viaCities: booking.viaCities || [],
            paymentMethod: booking.paymentMethod || "COD",
            tripType: booking.tripType || "oneWay",
            returnDate: booking.returnDate || null,
            returnTime: booking.returnTime || null,
            cancellationReason: booking.cancellationReason || "",
            advancePaid: booking.advancePaid || 0,
            remainingAmount: booking.remainingAmount || 0,
            advancePercentage: booking.advancePercentage || 0,
            finalAmount: booking.finalAmount || 0,
          };
        });

        setBookings(formattedBookings);
        setTotalBookings(formattedBookings.length);
        console.log("Bookings refreshed successfully");
      }
    } catch (error) {
      console.error("Error refreshing bookings:", error);
    }
  };

  // Fetch bookings from the database
  useEffect(() => {
    const fetchBookings = async () => {
      setIsLoading(true);
      try {
        let config = {};

        // If user is authenticated, use the token for authorization
        if (user && user.token) {
          config = {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          };
        }

        // Fetch bookings from the API
        const response = await axios.get(
          `${API_URL}/booking/admin/all`,
          config,
        );

        if (response.data && response.data.success) {
          // Transform the data to match our component structure
          const formattedBookings = response.data.data.map((booking) => {
            // Helper to build a readable location string from various possible fields
            const buildLocation = (type) => {
              const locObj =
                type === "from" ? booking.pickupLocation : booking.dropLocation;
              const cityField =
                type === "from"
                  ? booking.pickupCity || booking.fromCity
                  : booking.dropCity || booking.toCity;

              // Prioritize the from/to fields first as they contain proper location names
              const primaryField = type === "from" ? booking.from : booking.to;
              const secondaryField =
                type === "from" ? booking.pickupAddress : booking.dropAddress;

              // Prefer location object if it exists and has a name
              if (locObj?.name) {
                return `${locObj.name}${locObj.state ? `, ${locObj.state}` : ""}`;
              }

              // Check primary field (from/to) first - these usually contain proper location names
              if (
                primaryField &&
                primaryField.trim() &&
                primaryField !== "N/A" &&
                primaryField !== "Unknown" &&
                primaryField.length > 3 &&
                !primaryField.match(/^[a-z]{1,10}$/i)
              ) {
                return primaryField;
              }

              // Fallback to city field if valid
              if (cityField && cityField !== "N/A" && cityField.length > 2) {
                return cityField;
              }

              // Last resort: check secondary field (pickupAddress/dropAddress) if it looks like a real address
              if (
                secondaryField &&
                secondaryField.trim() &&
                secondaryField !== "N/A" &&
                secondaryField !== "Unknown" &&
                secondaryField.length > 3 &&
                !secondaryField.match(/^[a-z]{1,10}$/i)
              ) {
                return secondaryField;
              }

              return "Unknown Location";
            };

            const fromLocation = buildLocation("from");
            const toLocation = buildLocation("to");

            // Debug logging for address issues
            if (
              fromLocation === "Unknown Location" ||
              toLocation === "Unknown Location"
            ) {
              console.log("Address debug for booking:", booking.bookingId, {
                from: booking.from,
                to: booking.to,
                pickupAddress: booking.pickupAddress,
                dropAddress: booking.dropAddress,
                pickupLocation: booking.pickupLocation,
                dropLocation: booking.dropLocation,
              });
            }
            // Handle MongoDB cab type object
            const cabTypeName =
              booking.cabType?.name || booking.cabName || "Standard Cab";

            return {
              _id:
                booking._id ||
                booking.bookingId ||
                `BK${Math.floor(Math.random() * 10000)}`,
              bookingId: booking.bookingId,
              user: {
                _id: booking.user?._id || null,
                name:
                  booking.userName ||
                  booking.user?.name ||
                  booking.passengerDetails?.name ||
                  booking.corporateMemberId?.fullName ||
                  "Guest User",
                email:
                  booking.userEmail ||
                  booking.user?.email ||
                  booking.passengerDetails?.email ||
                  booking.corporateMemberId?.email ||
                  "guest@example.com",
                phone:
                  booking.userPhone ||
                  booking.user?.phone ||
                  booking.passengerDetails?.phone ||
                  booking.corporateMemberId?.mobileNumber ||
                  "",
              },
              from: fromLocation,
              to: toLocation,
              // Preserve original detailed location data
              pickupLocation: booking.pickupLocation,
              dropLocation: booking.dropLocation,
              pickupAddress: booking.pickupAddress || booking.pickupLocation?.address,
              dropAddress: booking.dropAddress || booking.dropLocation?.address,
              pickupAddressDetails: booking.pickupAddressDetails,
              dropAddressDetails: booking.dropAddressDetails,
              date: formatDateDDMMYYYY(new Date(
                booking.pickupDate ||
                  booking.travelDate ||
                  booking.date ||
                  Date.now(),
              )),
              pickupTime: formatTimeAMPM(booking.pickupTime || booking.time || booking.travelTime || ""),
              timestamp: new Date(
                booking.pickupDate ||
                  booking.travelDate ||
                  booking.date ||
                  Date.now(),
              ).getTime(),
              // Add service type detection with proper fallback logic
              serviceType: (() => {
                if (booking.serviceType && booking.serviceType !== "undefined") {
                  return booking.serviceType;
                }
                // Check for airport indicators
                if (booking.airport || booking.airportCode || booking.transferDirection) {
                  return "airport";
                }
                // Check for rental indicators
                if (booking.packageName || booking.rentalHours || booking.rentalKm) {
                  return "rental";
                }
                return "outstation";
              })(),
              packageName: booking.packageName || (booking.package && booking.package.name) || (booking.rentalPackageId && (booking.rentalPackageId.name || booking.rentalPackageId.packageName)) || "",
              rentalHours: booking.rentalHours || booking.hours || (booking.rentalPackageId && booking.rentalPackageId.hours) || 0,
              rentalKm: booking.rentalKm || booking.kilometers || (booking.rentalPackageId && booking.rentalPackageId.kilometers) || 0,
              extraFarePerHour: booking.extraFarePerHour,
              rentalPackageId: booking.rentalPackageId,
              paymentMethod: booking.paymentMethod,
              paymentId: booking.paymentId,
              status: booking.status || "pending",
              amount:
                booking.totalAmount || booking.fare || booking.amount || 0,
              cabType: cabTypeName,
              driver: booking.driver || null,

              // Trip type information (One Way vs Round Trip vs Multi-City)
              tripType: (() => {
                // Debug logging for trip type detection
                console.log("Admin Bookings - Trip Type Debug for booking:", booking.bookingId || booking._id, {
                  tripType: booking.tripType,
                  journeyType: booking.journeyType,
                  returnDate: booking.returnDate,
                  returnTime: booking.returnTime,
                  return_date: booking.return_date,
                  return_time: booking.return_time,
                  type: booking.type,
                  viaCities: booking.viaCities
                });

                // Check if booking has explicit journey type (primary field)
                if (booking.journeyType && booking.journeyType !== "undefined") {
                  if (booking.journeyType === "roundTrip") {
                    console.log("Using journeyType as Round Trip:", booking.journeyType);
                    return "roundTrip";
                  }
                  if (booking.journeyType === "oneWay") {
                    console.log("Using journeyType as One Way:", booking.journeyType);
                    return "oneWay";
                  }
                  // Support legacy formats if any exist
                  if (booking.journeyType === "round_trip" || booking.journeyType === "round-trip") {
                    console.log("Using legacy journeyType as Round Trip:", booking.journeyType);
                    return "roundTrip";
                  }
                  if (booking.journeyType === "one_way" || booking.journeyType === "one-way") {
                    console.log("Using legacy journeyType as One Way:", booking.journeyType);
                    return "oneWay";
                  }
                }

                // Check if booking has explicit trip type (fallback)
                if (booking.tripType && booking.tripType !== "undefined") {
                  console.log("Using explicit tripType:", booking.tripType);
                  return booking.tripType;
                }

                // Check for round trip indicators (multiple possible field names)
                if (booking.returnDate || booking.returnTime ||
                    booking.return_date || booking.return_time) {
                  console.log("Detected as Round Trip based on return date/time");
                  return "roundTrip";
                }

                // Check for via cities (multi-city trip)
                if (booking.viaCities && booking.viaCities.length > 0) {
                  console.log("Detected as Multi-City based on viaCities");
                  return "multiCity";
                }

                // Check for journey type patterns that indicate round trip
                if (booking.type === "round-trip" || booking.type === "roundTrip") {
                  console.log("Detected as Round Trip based on type field");
                  return "roundTrip";
                }

                // Default to one way
                console.log("Defaulting to One Way");
                return "oneWay";
              })(),

              // Via cities information
              viaCities: booking.viaCities || [],
              returnDate: booking.returnDate || null,
              returnTime: booking.returnTime || null,
              cancellationReason: booking.cancellationReason || "",
              advancePaid: booking.advancePaid || 0,
              remainingAmount: booking.remainingAmount || 0,
              advancePercentage: booking.advancePercentage || 0,
              finalAmount: booking.finalAmount || 0,
              selectedAddOns: booking.selectedAddOns || [],
              addOnsCost: booking.addOnsCost || 0,
            };
          });

          setBookings(formattedBookings);
          setTotalBookings(response.data.total || formattedBookings.length);
        } else {
          // If API call fails or returns no data, try localStorage as fallback
          tryLocalStorageFallback();
        }
      } catch (error) {
        console.error("Error fetching bookings:", error);
        // If API call fails, try localStorage as fallback
        tryLocalStorageFallback();
      } finally {
        setIsLoading(false);
      }
    };

    // Fallback to localStorage when API fails
    const tryLocalStorageFallback = () => {
      try {
        // Try to get all bookings from localStorage
        const userBookings = JSON.parse(
          localStorage.getItem("userBookings") || "[]",
        );
        if (userBookings.length > 0) {
          const formattedBookings = userBookings.map((booking) => ({
            _id:
              booking.id ||
              booking.bookingId ||
              `BK${Math.floor(Math.random() * 10000)}`,
            user: {
              _id: booking.user?._id || null,
              name:
                booking.userName ||
                booking.user?.name ||
                booking.passengerDetails?.name ||
                booking.corporateMemberId?.fullName ||
                "Guest User",
              email:
                booking.userEmail ||
                booking.user?.email ||
                booking.passengerDetails?.email ||
                booking.corporateMemberId?.email ||
                "guest@example.com",
              phone:
                booking.userPhone ||
                booking.user?.phone ||
                booking.passengerDetails?.phone ||
                booking.corporateMemberId?.mobileNumber ||
                "",
            },
            from:
              booking.from ||
              booking.pickupLocation ||
              booking.fromCity ||
              "",
            to: booking.to || booking.dropLocation || booking.toCity || "",
            // Preserve detailed location data
            pickupLocation: booking.pickupLocation,
            dropLocation: booking.dropLocation,
            pickupAddress: booking.pickupAddress || booking.pickupLocation?.address,
            dropAddress: booking.dropAddress || booking.dropLocation?.address,
            pickupAddressDetails: booking.pickupAddressDetails,
            dropAddressDetails: booking.dropAddressDetails,
            date: formatDateDDMMYYYY(new Date(
              booking.date ||
                booking.pickupDate ||
                booking.travelDate ||
                Date.now(),
            )),
            // Add service type detection with proper fallback logic
            serviceType: (() => {
              if (booking.serviceType && booking.serviceType !== "undefined") {
                return booking.serviceType;
              }
              // Check for airport indicators
              if (booking.airport || booking.airportCode || booking.transferDirection) {
                return "airport";
              }
              // Check for rental indicators
              if (booking.packageName || booking.rentalHours || booking.rentalKm) {
                return "rental";
              }
              return "outstation";
            })(),
            packageName: booking.packageName || (booking.package && booking.package.name) || (booking.rentalPackageId && (booking.rentalPackageId.name || booking.rentalPackageId.packageName)) || "",
            rentalHours: booking.rentalHours || booking.hours || (booking.rentalPackageId && booking.rentalPackageId.hours) || 0,
            rentalKm: booking.rentalKm || booking.kilometers || (booking.rentalPackageId && booking.rentalPackageId.kilometers) || 0,
            extraFarePerHour: booking.extraFarePerHour,
            rentalPackageId: booking.rentalPackageId,
            paymentMethod: booking.paymentMethod,
            paymentId: booking.paymentId,
            status: booking.status || "pending",
            amount: booking.amount || booking.totalAmount || booking.fare || 0,
            cabType: booking.cabType || booking.cabName || "Standard Cab",
            driver: booking.driver || null,

            // Trip type information
            tripType: (() => {
              if (booking.tripType && booking.tripType !== "undefined") {
                return booking.tripType;
              }
              // Check for round trip indicators (multiple possible field names)
              if (booking.returnDate || booking.returnTime ||
                  booking.return_date || booking.return_time ||
                  booking.journeyType === "round_trip" ||
                  booking.journeyType === "roundTrip" ||
                  booking.journeyType === "round-trip" ||
                  booking.type === "round-trip" || booking.type === "roundTrip") {
                return "roundTrip";
              }
              if (booking.viaCities && booking.viaCities.length > 0) {
                return "multiCity";
              }
              return "oneWay";
            })(),
            viaCities: booking.viaCities || [],
            returnDate: booking.returnDate || null,
            returnTime: booking.returnTime || null,
            advancePaid: booking.advancePaid || 0,
            remainingAmount: booking.remainingAmount || 0,
            advancePercentage: booking.advancePercentage || 0,
            finalAmount: booking.finalAmount || 0,
            selectedAddOns: booking.selectedAddOns || [],
            addOnsCost: booking.addOnsCost || 0,
          }));

          setBookings(formattedBookings);
          setTotalBookings(formattedBookings.length);
        } else {
          // If no bookings in localStorage, create demo data
          createDemoData();
        }
      } catch (error) {
        console.error("Error using localStorage fallback:", error);
        createDemoData();
      }
    };

    // No demo bookings: ensure page shows only real data or empty state when API/localStorage fail
    const createDemoData = () => {
      setBookings([]);
      setTotalBookings(0);
    };

    fetchBookings();
  }, [user, API_URL, page, limit, statusFilter]);

  // Fetch all available drivers
  useEffect(() => {
    const fetchDrivers = async () => {
      // Only fetch if needed
      if (drivers.length === 0) {
        try {
          const config =
            user && user.token
              ? {
                  headers: {
                    Authorization: `Bearer ${user.token}`,
                  },
                }
              : {};

          // Fetch both drivers and cab types to map them properly
          // First fetch drivers
          const driversResponse = await axios.get(
            `${DRIVER_API_URL}/available`,
            config,
          );
          const availableDrivers = driversResponse.data.data || [];

          // Then fetch cab types to map IDs to names
          let cabTypesMap = {};
          let cabTypesFetched = false;

          // Step 1: Try the new dedicated cab type endpoint
          try {
            const cabTypesResponse = await axios.get(
              `${API_URL}/cabtype/all`,
              config,
            );
            if (cabTypesResponse.data && cabTypesResponse.data.data) {
              cabTypesResponse.data.data.forEach((cabType) => {
                cabTypesMap[cabType._id] = cabType.name;
              });
              cabTypesFetched = true;
            }
          } catch (error) {
            console.error("Error fetching from /cabtype/all endpoint:", error);
          }

          // Step 3: Last resort - try the /cab endpoint
          if (!cabTypesFetched) {
            try {
              const fallbackResponse = await axios.get(
                `${API_URL}/cab`,
                config,
              );
              if (fallbackResponse.data && fallbackResponse.data.data) {
                fallbackResponse.data.data.forEach((cabType) => {
                  cabTypesMap[cabType._id] = cabType.name;
                });
              }
            } catch (fallbackError) {
              console.error(
                "Error fetching from all cab type endpoints:",
                fallbackError,
              );
              // Create a default mapping if all else fails
            }
          }

          setCabTypes(cabTypesMap);

          // Filter to only admin drivers (exclude vendor drivers)
          const adminDrivers = availableDrivers.filter((driver) => {
            // Exclude vendor drivers - only include admin drivers
            return !driver.vendorId && !driver.vendor;
          });

          // Format the admin drivers data
          const formattedDrivers = adminDrivers.map((driver) => {
            // Get the vehicle type ID and name from the driver record
            let vehicleTypeId = null;
            let vehicleTypeName = "Unknown";

            // Handle different possible driver data structures
            if (driver.vehicleType) {
              if (typeof driver.vehicleType === "string") {
                // ID reference directly
                vehicleTypeId = driver.vehicleType;
              } else if (driver.vehicleType._id) {
                // Populated mongoose object
                vehicleTypeId = driver.vehicleType._id;
                // If the object has a name, use it directly
                if (driver.vehicleType.name) {
                  vehicleTypeName = driver.vehicleType.name;
                }
              }
            }

            // Map driver data to our component format
            return {
              id: driver._id,
              name: driver.name || "Unknown Driver",
              phone: driver.phone || "",
              email: driver.email || "",
              status: driver.status || "available",
              carType: driver.vehicle?.model || vehicleTypeName || "Standard",
              carNumber: driver.vehicle?.registrationNumber || "",
              vehicleTypeId: vehicleTypeId,
              location: driver.location || "",
              rating: driver.rating || 4.5,
            };
          });

          // Update state with the formatted driver data
          setDrivers(formattedDrivers);
        } catch (error) {
          console.error("Error fetching drivers:", error);
          // Set some demo drivers if API fails
          if (drivers.length === 0) {
            setDrivers([
              {
                id: "D001",
                name: "John Driver",
                phone: "9876543210",
                status: "available",
                carType: "Sedan",
                carNumber: "DL-01-AB-1234",
                rating: 4.7,
              },
              {
                id: "D002",
                name: "Mary Wheels",
                phone: "8765432109",
                status: "available",
                carType: "SUV",
                carNumber: "DL-02-CD-5678",
                rating: 4.9,
              },
              {
                id: "D003",
                name: "Sam Speed",
                phone: "7654321098",
                status: "available",
                carType: "Hatchback",
                carNumber: "DL-03-EF-9012",
                rating: 4.5,
              },
            ]);
          }
        }
      } else {
        // Drivers already loaded, skipping fetch
      }
    };

    fetchDrivers();
  }, [drivers.length, user, DRIVER_API_URL, API_URL]);

  // Fetch all available vendors
  useEffect(() => {
    const fetchVendors = async () => {
      if (vendors.length === 0) {
        try {
          const config = user && user.token
            ? {
                headers: {
                  Authorization: `Bearer ${user.token}`,
                },
              }
            : {};

          const vendorsResponse = await axios.get(
            `${API_URL}/admin/vendors?page=1&limit=100`,
            config
          );

          console.log('Vendor API response:', vendorsResponse.data);

          if (vendorsResponse.data && (vendorsResponse.data.success || vendorsResponse.data.status)) {
            if (vendorsResponse.data.data && vendorsResponse.data.data.vendors) {
              console.log('Setting vendors from data.vendors:', vendorsResponse.data.data.vendors);
              setVendors(vendorsResponse.data.data.vendors);
            } else if (vendorsResponse.data.vendors) {
              console.log('Setting vendors from direct vendors field:', vendorsResponse.data.vendors);
              setVendors(vendorsResponse.data.vendors);
            } else if (vendorsResponse.data.data && Array.isArray(vendorsResponse.data.data)) {
              console.log('Setting vendors from data array directly:', vendorsResponse.data.data);
              setVendors(vendorsResponse.data.data);
            } else {
              console.log('No vendors found in response structure');
              setVendors([]);
            }
          } else {
            console.log('Vendor API response not successful');
            setVendors([]);
          }
        } catch (error) {
          console.error("Error fetching vendors:", error);
          setVendors([]);
        }
      }
    };

    fetchVendors();
  }, [vendors.length, user, API_URL]);

  // Fetch commission rate for each vendor (admin view) and precise wallet totals via admin drivers API
  useEffect(() => {
    const fetchVendorAuxData = async () => {
      if (!vendors || vendors.length === 0) return;
      try {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        const token = storedUser?.token || storedUser?.data?.token;
        if (!token) return;
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const commissionUpdates = {};
        const walletUpdates = {};
        await Promise.all(
          vendors.map(async (vendor) => {
            if (!vendor || !vendor._id) return;
            try {
              if (!vendorCommissionMap[vendor._id]) {
                const res = await axios.get(`${API_URL}/commission/admin/vendor/${vendor._id}/settings`, config);
                if (res.data && (res.data.success || res.data.status) && res.data.data) {
                  const eff = res.data.data.commissionSettings?.effectiveRate ??
                              res.data.data.commissionStructure?.vendorRate ?? null;
                  commissionUpdates[vendor._id] = { effectiveRate: eff };
                }
              }
            } catch (_) {}

            try {
              if (vendorWalletMap[vendor._id] == null) {
                const sumRes = await axios.get(`${API_URL}/admin/vendors/${vendor._id}/wallet/summary`, config);
                const total = sumRes.data?.data?.totalCommissionEarned ?? 0;
                walletUpdates[vendor._id] = Number(total) || 0;
              }
            } catch (_) {}
          })
        );
        if (Object.keys(commissionUpdates).length > 0) {
          setVendorCommissionMap((prev) => ({ ...prev, ...commissionUpdates }));
        }
        if (Object.keys(walletUpdates).length > 0) {
          setVendorWalletMap((prev) => ({ ...prev, ...walletUpdates }));
        }
      } catch (_) {}
    };
    fetchVendorAuxData();
  }, [vendors, API_URL]);

  // Manual vendor fetching function
  const fetchVendorsManually = async () => {
    try {
      console.log('Manually fetching vendors...');
      // Get token from stored user object
      const storedUser = JSON.parse(localStorage.getItem('user'));
      const token = storedUser?.token || storedUser?.data?.token;

      if (!token) {
        console.error('No token available for vendor fetching');
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const vendorsResponse = await axios.get(
        `${API_URL}/admin/vendors?page=1&limit=100`,
        config
      );

      console.log('Manual vendor API response:', vendorsResponse.data);

      if (vendorsResponse.data && (vendorsResponse.data.success || vendorsResponse.data.status)) {
        if (vendorsResponse.data.data && vendorsResponse.data.data.vendors) {
          console.log('Setting vendors from data.vendors:', vendorsResponse.data.data.vendors);
          setVendors(vendorsResponse.data.data.vendors);
        } else if (vendorsResponse.data.vendors) {
          console.log('Setting vendors from direct vendors field:', vendorsResponse.data.vendors);
          setVendors(vendorsResponse.data.vendors);
        } else if (vendorsResponse.data.data && Array.isArray(vendorsResponse.data.data)) {
          console.log('Setting vendors from data array directly:', vendorsResponse.data.data);
          setVendors(vendorsResponse.data.data);
        } else {
          console.log('No vendors found in response structure');
          setVendors([]);
        }
      } else {
        console.log('Vendor API response not successful');
        setVendors([]);
      }
    } catch (error) {
      console.error('Error manually fetching vendors:', error.response?.data || error.message);
      setVendors([]);
    }
  };

  // Handle status change click - opens confirmation modal
  const handleStatusChange = (bookingId, newStatus) => {
    const booking = bookings.find((b) => b._id === bookingId);
    if (!booking) return;

    setPendingStatusChange({ bookingId, newStatus });
    setSelectedBooking(booking);
    setShowStatusModal(true);
  };

  // Update booking status after confirmation
  const updateBookingStatus = async (bookingId, newStatus) => {
    // Find the booking to check current status
    const booking = bookings.find((b) => b._id === bookingId);
    if (!booking) {
      toast.error("Booking not found");
      return;
    }

    // Prevent changing status of completed or cancelled bookings
    if (booking.status === "completed" || booking.status === "cancelled") {
      toast.error(
        `Cannot change status of ${booking.status} booking. ` +
          `Final booking states cannot be modified.`,
      );
      return;
    }

    // Prevent going backwards in status progression
    if (booking.status === "confirmed" && newStatus === "pending") {
      toast.error(
        "Cannot change confirmed booking back to pending. " +
          "Status can only progress forward: pending → confirmed → completed."
      );
      return;
    }

    // Add booking to updating set
    setUpdatingBookings((prev) => new Set([...prev, bookingId]));

    // Optimistic update: update UI immediately
    setBookings((prevBookings) =>
      prevBookings.map((booking) =>
        booking._id === bookingId
          ? { ...booking, status: newStatus, updatedAt: new Date() }
          : booking,
      ),
    );

    try {
      let config = {};

      // If user is authenticated, use the token for authorization
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
      }

      // Update the booking status on the server
      const response = await axios.put(
        `${API_URL}/booking/${bookingId}`,
        { status: newStatus },
        config,
      );

      if (response.data && response.data.success) {
        if (newStatus !== 'confirmed') {
          toast.success(
            `Booking status updated to ${newStatus.toUpperCase()} successfully!`,
          );
        }

        // Auto-assign driver when booking is confirmed from pending
        if (newStatus === 'confirmed' && (booking.status === 'pending' || booking.status === 'approved_by_corporate')) {
          handleAutoAssign(bookingId);
        }
      } else {
        throw new Error(response.data?.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating booking status:", error);

      // Check for final status protection (completed/cancelled cannot be changed)
      if (
        error.response?.status === 403 &&
        (error.response?.data?.finalStatus ||
          error.response?.data?.driverControlled)
      ) {
        // Update booking data to reflect status protection and show modal
        const booking = bookings.find((b) => b._id === bookingId);
        if (booking) {
          setSelectedBooking({
            ...booking,
            statusChangedBy: error.response.data.statusChangedBy || "driver",
          });
          setShowStatusModal(true);
        }
        toast.error(error.response.data.message);
        return;
      }

      // Check other specific error types
      if (error.response?.status === 401) {
        toast.error("Authentication required. Please login again.");
      } else if (error.response?.status === 403) {
        toast.error("You do not have permission to update booking status.");
      } else if (error.response?.status === 404) {
        toast.error("Booking not found.");
      } else {
        // For development/demo: still update UI locally but show warning
        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking._id === bookingId
              ? { ...booking, status: newStatus }
              : booking,
          ),
        );
        toast.warning(
          `Status updated locally to ${newStatus.toUpperCase()}. Server connection issue: ${error.message}`,
        );
      }
    } finally {
      // Remove booking from updating set
      setUpdatingBookings((prev) => {
        const newSet = new Set(prev);
        newSet.delete(bookingId);
        return newSet;
      });
    }
  };

  // Close status confirmation modal
  const closeStatusModal = () => {
    setShowStatusModal(false);
    setPendingStatusChange(null);
    setSelectedBooking(null);
  };

  // Open driver assignment modal
  const openDriverAssignModal = (booking) => {
    console.log('Opening assignment modal for booking:', booking._id);
    console.log('Available drivers:', drivers.length);
    console.log('Available vendors:', vendors.length);
    console.log('Vendors data:', vendors);
    console.log('Current assignment type:', assignmentType);

    // Force refresh vendors when modal opens
    if (vendors.length === 0) {
      fetchVendorsManually();
    }

    // Reset assignment type to driver tab by default
    setAssignmentType("driver");
    setSelectedBooking(booking);
    setShowDriverModal(true);
  };

  // Close driver assignment modal
  const closeDriverAssignModal = () => {
    setSelectedBooking(null);
    setShowDriverModal(false);
  };

  // Check driver wallet balance and commission requirements
  const checkDriverWalletBalance = async (driverId, bookingAmount) => {
    try {
      console.log("🔍 Checking wallet for driver:", driverId);

      // Validate driver ID
      if (!driverId) {
        return { success: false, error: "Driver ID is required" };
      }

      let config = {};
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
      } else {
        return { success: false, error: "Authentication token missing" };
      }

      console.log("📡 Making wallet API call to:", `${API_URL}/wallet/driver/${driverId}`);

      // First, verify driver exists
      try {
        const driverCheckResponse = await axios.get(
          `${API_URL}/driver/${driverId}`,
          config,
        );
        if (!driverCheckResponse.data.success) {
          return { success: false, error: "Driver not found in system" };
        }
        console.log("✅ Driver exists:", driverCheckResponse.data.data.name);
      } catch (driverError) {
        console.error("❌ Driver verification failed:", driverError);
        return { success: false, error: "Driver not found or inaccessible" };
      }

      // Get driver wallet details
      const walletResponse = await axios.get(
        `${API_URL}/wallet/driver/${driverId}`,
        config,
      );

      console.log("���� Wallet response:", walletResponse.data);

      // Get commission rate for this booking
      const commissionResponse = await axios.get(
        `${API_URL}/commission/rate?driverId=${driverId}`,
        config,
      );

      console.log("📊 Commission response:", commissionResponse.data);

      if (walletResponse.data.success && commissionResponse.data.success) {
        const walletBalance = Number(walletResponse.data.data.balance) || 0;
        const commissionRate = Number(commissionResponse.data.data.commissionRate) || 10;
        const bookingAmountNumber = Number(bookingAmount) || 0;
        const requiredCommission = Math.round(
          (bookingAmountNumber * commissionRate) / 100,
        );
        const driverEarnings = bookingAmountNumber - requiredCommission;
        // For online payments: Driver receives 90%, pending commission auto-deducted if negative balance
        const pendingCommission = walletBalance < 0 ? Math.abs(walletBalance) : 0;
        const effectiveEarnings = Math.max(0, driverEarnings - pendingCommission);
        const newBalance = walletBalance + driverEarnings;

        console.log("💳 Wallet calculation:", {
          currentBalance: walletBalance,
          bookingAmount: bookingAmountNumber,
          commissionRate,
          requiredCommission,
          driverEarnings,
          newBalance,
        });

        return {
          success: true,
          walletBalance,
          commissionRate,
          requiredCommission,
          driverEarnings,
          newBalance,
          canAssign: true, // Online payments are always assignable
        };
      }

      return { success: false, error: "Failed to fetch wallet details" };
    } catch (error) {
      console.error("❌ Error checking wallet balance:", error);
      console.error("❌ Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config,
      });

      if (error.response?.status === 404) {
        return { success: false, error: "Driver wallet not found - driver may not exist" };
      }
      if (error.response?.status === 401) {
        return { success: false, error: "Authentication failed - please log in again" };
      }

      return { success: false, error: error.response?.data?.message || error.message };
    }
  };

  // Fetch wallet data for drivers and cache it to avoid duplicate API calls
  const fetchAndCacheDriverWallets = async (driverIds) => {
    if (!driverIds || driverIds.length === 0) return;

    try {
      let config = {};
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
      }

      const walletMap = { ...driverWalletMap };

      // Only fetch wallet data for drivers we haven't cached yet
      const driversToFetch = driverIds.filter(id => !walletMap[id]);

      if (driversToFetch.length === 0) return;

      // Fetch wallet and commission data for all drivers at once
      const walletRequests = driversToFetch.map(driverId =>
        axios.get(`${API_URL}/wallet/driver/${driverId}`, config).catch(() => ({ data: { success: false } }))
      );

      const commissionRequests = driversToFetch.map(driverId =>
        axios.get(`${API_URL}/commission/rate?driverId=${driverId}`, config).catch(() => ({ data: { success: false } }))
      );

      const walletResponses = await Promise.all(walletRequests);
      const commissionResponses = await Promise.all(commissionRequests);

      // Process responses and build wallet map
      driversToFetch.forEach((driverId, index) => {
        const walletResp = walletResponses[index];
        const commissionResp = commissionResponses[index];

        if (walletResp.data?.success && commissionResp.data?.success) {
          walletMap[driverId] = {
            walletBalance: walletResp.data.data.balance || 0,
            commissionRate: commissionResp.data.data.commissionRate || 10,
          };
        }
      });

      setDriverWalletMap(walletMap);
    } catch (error) {
      console.error("Error fetching driver wallets:", error);
    }
  };

  // Fetch wallet data when drivers are loaded
  useEffect(() => {
    if (drivers.length > 0 && Object.keys(driverWalletMap).length < drivers.length) {
      const driverIds = drivers.map(d => d.id || d._id);
      fetchAndCacheDriverWallets(driverIds);
    }
  }, [drivers.length]);

  // Assign driver to booking
  const assignDriver = async (driverId) => {
    if (!selectedBooking) return;

    // Find the selected driver
    const selectedDriver = drivers.find(
      (driver) => driver.id === driverId || driver._id === driverId,
    );
    if (!selectedDriver) return;

    // Use MongoDB _id instead of id
    const mongoDbDriverId = selectedDriver._id || driverId;

    try {
      setIsLoading(true);
      let config = {};

      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        };
      }

      // Determine driverSource and vendorId if available
      const driverSource = selectedDriver.vendorId || selectedDriver.vendor ? "vendor" : "admin";
      const vendorId = selectedDriver.vendorId || null;

      // Check if this is an online booking that requires commission check
      if (selectedBooking.paymentMethod === "ONLINE") {
        console.log("Checking wallet balance for online booking...");

        // Get booking amount from various possible fields
        const bookingAmount = getBookingAmount(selectedBooking);

        console.log("🔍 Booking amount fields:", {
          totalAmount: getBookingAmount(selectedBooking),
          fare: selectedBooking.fare,
          amount: selectedBooking.amount,
          finalAmount: selectedBooking.finalAmount,
          selectedAmount: bookingAmount
        });

        const walletCheck = await checkDriverWalletBalance(
          mongoDbDriverId,
          bookingAmount,
        );

        if (!walletCheck.success) {
          toast.error(`Failed to check driver wallet: ${walletCheck.error}`);
          return;
        }

        if (!walletCheck.canAssign) {
          // Show detailed warning with wallet information
          const confirmAssign = window.confirm(
            `⚠️ WALLET BALANCE WARNING ��️\n\n` +
              `Driver: ${selectedDriver.name}\n` +
              `Current Wallet Balance: ₹${walletCheck.walletBalance}\n` +
              `Booking Amount: ₹${bookingAmount}\n` +
              `Required Commission (${walletCheck.commissionRate}%): ��${walletCheck.requiredCommission}\n` +
              `Driver Earnings: ₹${walletCheck.driverEarnings}\n` +
              `Balance After Commission: ₹${walletCheck.newBalance}\n\n` +
              `⚠️ This driver's balance will be critically low (��${walletCheck.newBalance}).\n\n` +
              `Do you still want to assign this driver?`,
          );

          if (!confirmAssign) {
            toast.info("Driver assignment cancelled due to low wallet balance");
            return;
          }
        } else {
          // Show success info about wallet status
          toast.success(
            `✅ Wallet Check Passed\n` +
              `Driver: ${selectedDriver.name}\n` +
              `Current Balance: ₹${walletCheck.walletBalance}\n` +
              `Balance After Ride: ₹${walletCheck.newBalance}`,
            { autoClose: 3000 },
          );
        }
      }

      // Attempt to assign driver with retries
      let attempt = 0;
      const maxAttempts = 3;
      let apiSuccess = false;

      // Prepare payload and log it for debugging
      const payload = {
        bookingId: selectedBooking._id,
        driverId: mongoDbDriverId,
      };
      if (driverSource === "vendor") {
        payload.driverSource = "vendor";
        if (vendorId) payload.vendorId = vendorId;
      }

      console.log("Assign driver payload:", payload);

      while (attempt < maxAttempts && !apiSuccess) {
        try {
          // Make API call to assign driver using only the expected parameters
          const response = await axios.put(
            `${API_URL}/booking/assign-driver`,
            payload,
            config,
          );

          if (response.data && response.data.success) {
            apiSuccess = true;
          } else {
            // If the server responded with success:false, include response message
            const errMsg = response.data?.message || "Invalid API response format";
            throw new Error(errMsg);
          }
        } catch (apiError) {
          console.error(`Attempt ${attempt + 1}/${maxAttempts} failed:`, apiError);
          // Log server response if available
          if (apiError.response) {
            console.error("Server response:", apiError.response.data);
          }

          attempt++;

          if (attempt < maxAttempts) {
            // Wait before retrying (exponential backoff)
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          } else {
            // On final failure, show server-provided message if present
            const serverMessage = apiError.response?.data?.message || apiError.message;
            toast.error(`Failed to assign driver: ${serverMessage}`);
          }
        }
      }

      // Update driver status to assigned
      setDrivers((prevDrivers) =>
        prevDrivers.map((driver) =>
          driver.id === driverId ? { ...driver, status: "assigned" } : driver,
        ),
      );

      // Update the booking with the assigned driver
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking._id === selectedBooking._id
            ? {
                ...booking,
                status: "confirmed",
                driver: {
                  id: selectedDriver.id,
                  name: selectedDriver.name,
                  phone: selectedDriver.phone,
                  carNumber: selectedDriver.carNumber,
                  status: "assigned",
                },
              }
            : booking,
        ),
      );

      if (apiSuccess) {
        toast.success(
          `Driver ${selectedDriver.name} assigned to booking successfully`,
        );
        // Refresh bookings data to ensure confirmed bookings show up properly
        setTimeout(() => {
          refreshBookings();
        }, 1000);
      } else {
        // Server not reachable after retries — assignment applied locally only.
        // Suppress user-facing toast here to avoid confusion on the admin bookings page.
        console.warn("Unable to connect to server. Driver assigned locally only.");
      }
    } catch (error) {
      console.error("Error in assignment process:", error);
      toast.error(`Failed to assign driver. Please try again.`);
    } finally {
      setIsLoading(false);
      closeDriverAssignModal();
    }
  };

  // Assign vendor to booking
  const assignVendor = async (vendorId) => {
    if (!selectedBooking) return;

    // Find the selected vendor
    const selectedVendor = vendors.find(
      (vendor) => vendor._id === vendorId
    );
    if (!selectedVendor) return;

    try {
      setIsLoading(true);
      let config = {};

      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        };
      }

      // Make API call to assign vendor
      const response = await axios.post(
        `${API_URL}/booking/${selectedBooking._id}/assign-vendor`,
        { vendorId: vendorId },
        config
      );

      if (response.data && response.data.success) {
        // Update the booking with the assigned vendor
        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking._id === selectedBooking._id
              ? {
                  ...booking,
                  status: "assigned_to_vendor",
                  vendor: {
                    _id: selectedVendor._id,
                    name: selectedVendor.fullName || selectedVendor.name,
                    email: selectedVendor.email,
                    phone: selectedVendor.phone,
                  },
                }
              : booking,
          ),
        );

        toast.success(
          `Booking assigned to vendor ${selectedVendor.fullName || selectedVendor.name} successfully. The vendor will assign a driver through their dashboard.`
        );

        // Refresh bookings data
        setTimeout(() => {
          refreshBookings();
        }, 1000);
      } else {
        toast.error("Failed to assign vendor. Please try again.");
      }
    } catch (error) {
      console.error("Error assigning vendor:", error);
      toast.error(`Failed to assign vendor: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsLoading(false);
      closeDriverAssignModal();
    }
  };

  // Remove assigned driver
  const removeDriver = async (bookingId) => {
    // Find the booking
    const booking = bookings.find((b) => b._id === bookingId);
    if (!booking || !booking.driver) {
      toast.error("No driver assigned to this booking");
      return;
    }

    // Check if booking is in a final state that cannot be modified
    if (booking.status === "completed" || booking.status === "cancelled") {
      toast.error(
        `Cannot remove driver from ${booking.status} booking. ` +
          `Driver assignment is locked for final booking states.`,
      );
      return;
    }

    try {
      setIsLoading(true);
      let config = {};

      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        };
      } else {
        toast.error("Authentication required");
        setIsLoading(false);
        return;
      }

      // Call the remove driver API
      const response = await axios.put(
        `${API_URL}/booking/${bookingId}/remove-driver`,
        {},
        config,
      );

      if (response.data && response.data.success) {
        console.log("Driver removed successfully via API");
      } else {
        throw new Error(response.data?.message || "Failed to remove driver");
      }

      // Update driver status back to available
      const driverToUpdate = drivers.find(
        (d) => d.phone === booking.driver.phone,
      );
      if (driverToUpdate) {
        setDrivers((prevDrivers) =>
          prevDrivers.map((driver) =>
            driver.id === driverToUpdate.id
              ? { ...driver, status: "available" }
              : driver,
          ),
        );
      }

      // Remove driver from booking
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking._id === bookingId
            ? { ...booking, driver: null, status: "confirmed" }
            : booking,
        ),
      );

      toast.success(`Driver removed from booking successfully`);
    } catch (error) {
      console.error("Error removing driver:", error);

      // Handle specific error cases
      if (error.response?.status === 404) {
        toast.error("Booking not found");
      } else if (error.response?.status === 400) {
        toast.error(error.response.data?.message || "Cannot remove driver from this booking");
      } else if (error.response?.status === 401) {
        toast.error("Authentication required");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to remove drivers");
      } else {
        toast.error(`Failed to remove driver: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Auto assign driver to booking
  const handleAutoAssign = async (bookingId) => {
    try {
      setAutoAssignLoading((prev) => ({ ...prev, [bookingId]: true }));

      const result = await dispatch(autoAssignDriver(bookingId)).unwrap();

      toast.success(
        `Auto assign started! Notified ${result.driversNotified} drivers and ${result.vendorsNotified || 0} vendors. Waiting for acceptance...`,
      );

      // Update booking status in local state
      setBookings((prevBookings) =>
        prevBookings.map((booking) =>
          booking._id === bookingId
            ? { ...booking, status: "searching_driver" }
            : booking,
        ),
      );
    } catch (error) {
      console.error("Error starting auto assign:", error);
      // Extract the actual error message from the rejected thunk
      const errorMessage = error?.message || error?.toString() || "Failed to start auto assign. Please try again.";
      toast.error(errorMessage);
    } finally {
      setAutoAssignLoading((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  // Retry auto assign - notify all available drivers
  const handleRetryAutoAssign = async (bookingId) => {
    try {
      setAutoAssignLoading((prev) => ({ ...prev, [bookingId]: true }));

      let config = {};
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        };
      } else {
        toast.error("Authentication required");
        setAutoAssignLoading((prev) => ({ ...prev, [bookingId]: false }));
        return;
      }

      const response = await axios.post(
        `${API_URL}/booking/${bookingId}/retry-auto-assign`,
        {},
        config,
      );

      if (response.data && response.data.success) {
        toast.success(
          `Retry started! Notified ${response.data.data.driversNotified} drivers and ${response.data.data.vendorsNotified || 0} vendors.`,
        );

        // Update booking status in local state
        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking._id === bookingId
              ? { ...booking, status: "searching_driver" }
              : booking,
          ),
        );
      } else {
        throw new Error(response.data?.message || "Retry failed");
      }
    } catch (error) {
      console.error("Error retrying auto assign:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to retry auto assign. Please try again.",
      );
    } finally {
      setAutoAssignLoading((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  // Stop searching driver - revert status to confirmed
  const handleStopSearching = async (bookingId) => {
    try {
      setAutoAssignLoading((prev) => ({ ...prev, [bookingId]: true }));

      let config = {};
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        };
      } else {
        toast.error("Authentication required");
        setAutoAssignLoading((prev) => ({ ...prev, [bookingId]: false }));
        return;
      }

      const response = await axios.post(
        `${API_URL}/booking/${bookingId}/stop-searching`,
        {},
        config,
      );

      if (response.data && response.data.success) {
        toast.success("Stopped driver search successfully!");

        // Update booking status in local state to confirmed
        setBookings((prevBookings) =>
          prevBookings.map((booking) =>
            booking._id === bookingId
              ? { ...booking, status: "confirmed", notifiedDrivers: [] }
              : booking,
          ),
        );
      } else {
        throw new Error(response.data?.message || "Failed to stop search");
      }
    } catch (error) {
      console.error("Error stopping search:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to stop search. Please try again.",
      );
    } finally {
      setAutoAssignLoading((prev) => ({ ...prev, [bookingId]: false }));
    }
  };

  // Helper function to update local state when removing a driver
  const updateLocalDriverRemoval = (bookingId, booking) => {
    // Update driver status back to available
    const driverToUpdate = drivers.find(
      (d) => d.phone === booking.driver.phone,
    );
    if (driverToUpdate) {
      setDrivers((prevDrivers) =>
        prevDrivers.map((driver) =>
          driver.id === driverToUpdate.id
            ? { ...driver, status: "available" }
            : driver,
        ),
      );
    }

    // Remove driver from booking
    setBookings((prevBookings) =>
      prevBookings.map((booking) =>
        booking._id === bookingId ? { ...booking, driver: null } : booking,
      ),
    );
  };

  // View ratings function
  const viewRatings = async (booking) => {
    try {
      setSelectedRatingData({ loading: true });
      setShowRatingModal(true);

      let config = {};
      if (user && user.token) {
        config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
      }

      try {
        // Fetch ratings for this booking
        const response = await axios.get(
          `${API_URL}/booking/${booking._id}/ratings`,
          config,
        );

        if (response.data && response.data.success) {
          setSelectedRatingData({
            booking: booking,
            ratings: response.data.data,
            loading: false,
          });
        } else {
          // Show modal with no ratings message
          setSelectedRatingData({
            booking: booking,
            ratings: null,
            loading: false,
            message: "No ratings available for this booking",
          });
        }
      } catch (apiError) {
        console.error("API Error fetching ratings:", apiError);
        setSelectedRatingData({
          booking: booking,
          ratings: null,
          loading: false,
          error: apiError.response?.data?.message || "Failed to fetch ratings. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error in viewRatings:", error);
      setSelectedRatingData({
        booking: booking,
        ratings: null,
        loading: false,
        error: "Failed to load ratings",
      });
    }
  };

  // Filter bookings based on search and status
  const allFilteredBookings = bookings.filter((booking) => {
    const matchesSearch =
      booking._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (booking.bookingId && booking.bookingId.toLowerCase().includes(searchTerm.toLowerCase())) ||
      booking.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.to.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = (() => {
      if (statusFilter === "all") return true;

      // For confirmed filter, only include confirmed status bookings
      if (statusFilter === "confirmed") {
        return booking.status === "confirmed";
      }

      if (statusFilter === "pending") {
        return (booking.status === "pending" || booking.status === "approved_by_corporate");
      }

      // For assigned filter, include assigned bookings with drivers
      if (statusFilter === "assigned") {
        return booking.status === "assigned" && booking.driver;
      }
      if (statusFilter === "assigned_to_vendor") {
        return booking.status === "assigned_to_vendor";
      }

      return booking.status === statusFilter;
    })();

    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    // Primary sort by timestamp
    if (a.timestamp !== b.timestamp) {
      return sortOrder === "asc" ? a.timestamp - b.timestamp : b.timestamp - a.timestamp;
    }
    // Secondary sort by time string
    const timeA = a.pickupTime || "";
    const timeB = b.pickupTime || "";
    return sortOrder === "asc" ? timeA.localeCompare(timeB) : timeB.localeCompare(timeA);
  });

  // Calculate pagination
  const totalFilteredBookings = allFilteredBookings.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const filteredBookings = allFilteredBookings.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-6 text-base admin-bookings-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Booking Management
          </h1>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Real-time customer ride bookings, status updates and assignments
          </p>
        </div>
      </div>

      {/* Search and Active Filter Display */}
      <div className="bg-white rounded-xl shadow-md p-4 mb-6 border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="w-full md:w-1/3">
            <label htmlFor="search" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  ></path>
                </svg>
              </div>
              <input
                id="search"
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search bookings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center bg-gray-100 rounded-lg px-4 py-2">
              <FaFilter className="text-gray-500 mr-2" />
              <span className="text-sm text-gray-600">Showing:</span>
              <span className="ml-2 text-sm font-semibold text-gray-800 capitalize">
                {statusFilter === "all" ? "All Bookings" : `${statusFilter} Bookings`}
              </span>
            </div>
            
            {/* Sort Order Toggle */}
            <div className="flex items-center bg-blue-50 rounded-lg border border-blue-100 px-3 py-2">
              <span className="text-sm text-blue-700 font-medium mr-2">Order:</span>
              <select 
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="bg-transparent text-sm font-bold text-blue-800 outline-none cursor-pointer"
              >
                <option value="asc">Oldest to Newest (Asc)</option>
                <option value="desc">Newest to Oldest (Desc)</option>
              </select>
            </div>

            {statusFilter !== "all" && (
              <button
                onClick={() => setStatusFilter("all")}
                className="text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" />
                Clear Filter
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Summary - Clickable Filter Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-9 gap-3 mb-8">
        <div
          className={`bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "all" ? "ring-2 ring-blue-500 bg-blue-50" : ""
          }`}
          onClick={() => setStatusFilter("all")}
        >
          <div className="flex items-center">
            <div className={`p-1.5 sm:p-2 rounded-full mr-1.5 sm:mr-2 ${statusFilter === "all" ? "bg-blue-500" : "bg-blue-100"}`}>
              <FaCalendarAlt className={statusFilter === "all" ? "text-white" : "text-blue-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] sm:text-xs">Total</p>
              <p className="text-lg sm:text-xl font-bold">{totalBookings}</p>
            </div>
          </div>
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "pending" ? "ring-2 ring-orange-500 bg-orange-50" : ""
          }`}
          onClick={() => setStatusFilter("pending")}
        >
          <div className="flex items-center">
            <div className={`p-1.5 sm:p-2 rounded-full mr-1.5 sm:mr-2 ${statusFilter === "pending" ? "bg-orange-500" : "bg-orange-100"}`}>
              <FaClock className={statusFilter === "pending" ? "text-white" : "text-orange-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] sm:text-xs">Pending</p>
              <p className="text-lg sm:text-xl font-bold">
                {bookings.filter((b) => (b.status === "pending" || b.status === "approved_by_corporate")).length}
              </p>
            </div>
          </div>
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "confirmed" ? "ring-2 ring-green-500 bg-green-50" : ""
          }`}
          onClick={() => setStatusFilter("confirmed")}
        >
          <div className="flex items-center">
            <div className={`p-1.5 sm:p-2 rounded-full mr-1.5 sm:mr-2 ${statusFilter === "confirmed" ? "bg-green-500" : "bg-green-100"}`}>
              <FaCheckCircle className={statusFilter === "confirmed" ? "text-white" : "text-green-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] sm:text-xs">Confirmed</p>
              <p className="text-lg sm:text-xl font-bold">
                {bookings.filter((b) => b.status === "confirmed").length}
              </p>
            </div>
          </div>
        </div>
        {/* NEW: Searching Driver Filter Card */}
        <div
          className={`bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "searching_driver" ? "ring-2 ring-indigo-500 bg-indigo-50" : ""
          }`}
          onClick={() => setStatusFilter("searching_driver")}
        >
          <div className="flex items-center">
            <div className={`p-1.5 sm:p-2 rounded-full mr-1.5 sm:mr-2 ${statusFilter === "searching_driver" ? "bg-indigo-500" : "bg-indigo-100"}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-[10px] sm:text-xs">Searching</p>
              <p className="text-lg sm:text-xl font-bold">
                {bookings.filter((b) => b.status === "searching_driver").length}
              </p>
            </div>
          </div>
          {statusFilter === "searching_driver" && (
            <div className="mt-1 text-[10px] text-indigo-600 font-medium">Active</div>
          )}
        </div>
        {/* NEW: Driver Not Found Filter Card */}
        <div
          className={`bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "driver_not_found" ? "ring-2 ring-rose-500 bg-rose-50" : ""
          }`}
          onClick={() => setStatusFilter("driver_not_found")}
        >
          <div className="flex items-center">
            <div className={`p-1.5 sm:p-2 rounded-full mr-1.5 sm:mr-2 ${statusFilter === "driver_not_found" ? "bg-rose-500" : "bg-rose-100"}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-[10px] sm:text-xs">No Driver</p>
              <p className="text-lg sm:text-xl font-bold">
                {bookings.filter((b) => b.status === "driver_not_found").length}
              </p>
            </div>
          </div>
          {statusFilter === "driver_not_found" && (
            <div className="mt-1 text-[10px] text-rose-600 font-medium">Active</div>
          )}
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "assigned" ? "ring-2 ring-teal-500 bg-teal-50" : ""
          }`}
          onClick={() => setStatusFilter("assigned")}
        >
          <div className="flex items-center">
            <div className={`p-1.5 sm:p-2 rounded-full mr-1.5 sm:mr-2 ${statusFilter === "assigned" ? "bg-teal-500" : "bg-teal-100"}`}>
              <FaUserTie className={statusFilter === "assigned" ? "text-white" : "text-teal-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] sm:text-xs">Assigned</p>
              <p className="text-lg sm:text-xl font-bold">
                {bookings.filter((b) => b.status === "assigned" && b.driver).length}
              </p>
            </div>
          </div>
        </div>
        {/* Assigned to Vendor Filter Card */}
        <div
          className={`bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "assigned_to_vendor" ? "ring-2 ring-cyan-500 bg-cyan-50" : ""
          }`}
          onClick={() => setStatusFilter("assigned_to_vendor")}
        >
          <div className="flex items-center">
            <div className={`p-1.5 sm:p-2 rounded-full mr-1.5 sm:mr-2 ${statusFilter === "assigned_to_vendor" ? "bg-cyan-500" : "bg-cyan-100"}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p className="text-gray-500 text-[10px] sm:text-xs">Vendor</p>
              <p className="text-lg sm:text-xl font-bold">
                {bookings.filter((b) => b.status === "assigned_to_vendor").length}
              </p>
            </div>
          </div>
          {statusFilter === "assigned_to_vendor" && (
            <div className="mt-1 text-[10px] text-cyan-600 font-medium">Active</div>
          )}
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "completed" ? "ring-2 ring-purple-500 bg-purple-50" : ""
          }`}
          onClick={() => setStatusFilter("completed")}
        >
          <div className="flex items-center">
            <div className={`p-1.5 sm:p-2 rounded-full mr-1.5 sm:mr-2 ${statusFilter === "completed" ? "bg-purple-500" : "bg-purple-100"}`}>
              <FaCheckCircle className={statusFilter === "completed" ? "text-white" : "text-purple-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] sm:text-xs">Completed</p>
              <p className="text-lg sm:text-xl font-bold">
                {bookings.filter((b) => b.status === "completed").length}
              </p>
            </div>
          </div>
        </div>
        <div
          className={`bg-white rounded-xl shadow-md p-3 sm:p-4 border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105 ${
            statusFilter === "cancelled" ? "ring-2 ring-red-500 bg-red-50" : ""
          }`}
          onClick={() => setStatusFilter("cancelled")}
        >
          <div className="flex items-center">
            <div className={`p-1.5 sm:p-2 rounded-full mr-1.5 sm:mr-2 ${statusFilter === "cancelled" ? "bg-red-500" : "bg-red-100"}`}>
              <FaTimes className={statusFilter === "cancelled" ? "text-white" : "text-red-600"} />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] sm:text-xs">Cancelled</p>
              <p className="text-lg sm:text-xl font-bold">
                {bookings.filter((b) => b.status === "cancelled").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 w-full">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <span className="ml-3 text-gray-600">Loading booking data...</span>
          </div>
        ) : (
          <div className="table-scroll-container overflow-x-auto max-h-[70vh] overflow-y-auto" style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: '2000px' }}>
              <table className="admin-table" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '120px' }} />
                <col style={{ width: '160px' }} />
                <col style={{ width: '240px' }} />
                <col style={{ width: '360px' }} />
                <col style={{ width: '250px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '200px' }} />
                <col style={{ width: '240px' }} />
                <col style={{ width: '250px' }} />
              </colgroup>
              <thead>
                <tr className="bg-slate-50">
                  <th className="py-3 px-4 text-left rounded-l-xl">DATE & TIME</th>
                  <th className="py-3 px-4 text-left">BOOKING ID</th>
                  <th className="py-3 px-4 text-left">USER DETAILS</th>
                  <th className="py-3 px-4 text-left">ROUTE</th>
                  <th className="py-3 px-4 text-left">SERVICE</th>
                  <th className="py-3 px-4 text-left">AMOUNT</th>
                  <th className="py-3 px-4 text-left">STATUS</th>
                  <th className="py-3 px-4 text-left">DRIVER & CAR TYPE</th>
                  <th className="py-3 px-4 text-right rounded-r-xl">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredBookings.length > 0 ? (
                  filteredBookings.map((booking) => {
                    return (
                      <tr key={booking._id}>
                      <td>
                        <div className="flex flex-col">
                          <span className="font-medium">{booking.date}</span>
                          {booking.pickupTime && (
                            <span className="text-sm text-blue-600 font-bold flex items-center mt-1">
                              <FaClock className="mr-1 w-3 h-4" />
                              {booking.pickupTime}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="font-mono text-sm">{booking.bookingId || booking._id}</td>
                      <td>
                        <div className="max-w-[220px]">
                          <div className="font-medium truncate" title={booking.user.name}>{booking.user.name}</div>
                          <div className="text-gray-500 text-sm truncate" title={booking.user.email}>
                            {booking.user.email}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-col gap-1 max-w-[320px]">
                          <div className="flex items-center justify-between gap-1 text-sm font-semibold text-blue-700 bg-blue-50 px-2.5 py-1.5 rounded-lg border border-blue-100">
                             <div className="flex items-center gap-1.5 min-w-0 flex-1">
                               <span className="truncate max-w-[130px] font-bold text-gray-900" title={booking.from}>{booking.from}</span>
                               <span className="text-blue-500 font-bold flex-shrink-0">→</span>
                               <span className="truncate max-w-[130px] font-bold text-gray-900" title={booking.to}>{booking.to}</span>
                             </div>
                             
                             {/* Address Detail Icon — opens full details dialog modal ONLY on click */}
                             <button
                               className="text-blue-600 hover:text-white hover:bg-blue-600 transition-all p-1 bg-blue-50/80 rounded-full border border-blue-200 flex-shrink-0 shadow-xs cursor-pointer active:scale-95"
                               onClick={(e) => {
                                 e.stopPropagation();
                                 setRouteDetailModalBooking(booking);
                               }}
                               tabIndex={0}
                               title="Click to view full address & add-on services"
                             >
                               <FaInfoCircle className="w-3.5 h-3.5" />
                             </button>
                          </div>

                          {/* Distinct pickup address if different from location name */}
                          {booking.pickupAddress && booking.pickupAddress.trim().toLowerCase() !== booking.from.trim().toLowerCase() && (
                            <div className="text-[11px] text-gray-500 leading-tight flex items-start gap-1 pl-1">
                              <span className="text-emerald-500 font-bold text-xs leading-none mt-0.5">•</span>
                              <span className="truncate max-w-[290px]" title={booking.pickupAddress}>
                                <strong className="text-gray-600">Pick:</strong> {booking.pickupAddress}
                              </span>
                            </div>
                          )}

                          {/* Distinct drop address if different from location name */}
                          {booking.dropAddress && booking.dropAddress.trim().toLowerCase() !== booking.to.trim().toLowerCase() && (
                            <div className="text-[11px] text-gray-500 leading-tight flex items-start gap-1 pl-1">
                              <span className="text-rose-500 font-bold text-xs leading-none mt-0.5">•</span>
                              <span className="truncate max-w-[290px]" title={booking.dropAddress}>
                                <strong className="text-gray-600">Drop:</strong> {booking.dropAddress}
                              </span>
                            </div>
                          )}
                          
                          {booking.serviceType === "rental" && (
                            <div className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded border border-green-100 font-medium mt-0.5 flex items-center gap-1 self-start">
                              📦 Rental City: {booking.fromCity || booking.from}
                            </div>
                          )}

                          {/* Multi-City via destinations badge below route */}
                          {(booking.tripType === "multiCity" || (booking.viaCities && booking.viaCities.length > 0)) && (
                            <div className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded border border-green-100 font-medium mt-1 flex items-center gap-1 self-start whitespace-normal break-words max-w-[320px]">
                              🌐 Multi-City (via {formatViaCitiesList(booking.viaCities)})
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <div className="space-y-1">
                          <div className="text-sm font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded whitespace-normal break-words">
                            🚗 {booking.cabType?.name ||
                              booking.cabType ||
                              booking.cabName ||
                              "Standard Cab"}
                          </div>



                  {/* Trip Type Information */}
                          <div className={`text-sm px-2 py-1 rounded font-bold whitespace-normal break-words ${
                            booking.serviceType === "airport"
                              ? "bg-orange-100 text-orange-800"
                              : booking.tripType === "roundTrip"
                              ? "bg-purple-100 text-purple-800"
                              : booking.tripType === "multiCity" || (booking.viaCities && booking.viaCities.length > 0)
                              ? "bg-green-100 text-green-800"
                              : "bg-blue-100 text-blue-800"
                          }`}>
                            {(() => {
                              // Handle airport transfer service type
                              if (booking.serviceType === "airport") {
                                  if (booking.transferDirection === "to" || booking.airportDirection === "to") {
                                    return "✈️ To Airport";
                                  } else if (booking.transferDirection === "from" || booking.airportDirection === "from") {
                                    return "🛬 From Airport";
                                  } else {
                                    return "✈️ Airport Transfer";
                                  }
                                }
                                // Handle rental service type
                                else if (booking.serviceType === "rental") {
                                  return "🚗 Local Rental";
                                }
                                // Handle regular trip types
                                else if (booking.tripType === "roundTrip") {
                                  return "🔄 Round Trip";
                                } else if (booking.tripType === "multiCity" || (booking.viaCities && booking.viaCities.length > 0)) {
                                  return "🌐 Multi-City";
                                } else {
                                  return "➡️ One Way";
                                }
                              })()}
                            </div>
  
                            {/* Return Date for Round Trips */}
                            {booking.tripType === "roundTrip" && booking.returnDate && (
                              <div className="text-sm text-purple-600 bg-purple-50 px-2 py-1 rounded whitespace-normal break-words">
                                Return: {formatReturnDate(booking.returnDate)}{booking.returnTime ? ` at ${booking.returnTime}` : ''}
                              </div>
                            )}
                            {booking.serviceType === "rental" && (
                              <div className="text-sm text-green-600 bg-green-50 px-2 py-1 rounded whitespace-normal break-words">
                                📦 {booking.packageName || "Rental Package"}
                                {(booking.rentalHours != null || booking.rentalKm != null) && (
                                  <div className="text-sm text-gray-600 mt-1 whitespace-normal break-words">
                                    {booking.rentalHours != null && `${booking.rentalHours} Hours`}
                                    {booking.rentalKm != null && ` / ${booking.rentalKm} KM`}
                                  </div>
                                )}
                                {booking.extraFarePerHour && (
                                  <div className="text-sm text-orange-600 mt-1 whitespace-normal break-words">
                                    Extra Fare: ₹{booking.extraFarePerHour}/Hour
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Compact Add-on Services Pill */}
                            {booking.selectedAddOns && booking.selectedAddOns.length > 0 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRouteDetailModalBooking(booking);
                                }}
                                className="w-full flex items-center justify-between px-2 py-1 mt-1 rounded-md bg-amber-50 hover:bg-amber-100 border border-amber-200/80 text-amber-900 text-xs font-medium transition-colors cursor-pointer group shadow-2xs"
                                title="Click to view add-on service details"
                              >
                                <span className="flex items-center gap-1 font-semibold text-amber-900 truncate">
                                  <span>✨</span>
                                  <span>{booking.selectedAddOns.length} Add-on{booking.selectedAddOns.length > 1 ? 's' : ''}</span>
                                </span>
                                <span className="font-bold text-amber-950 ml-1 flex-shrink-0">
                                  +₹{booking.addOnsCost || booking.selectedAddOns.reduce((s, a) => s + (Number(a.price) || 0), 0)}
                                </span>
                              </button>
                            )}
                          </div>
                        </td>
  
                        <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          <div className="flex flex-col items-start space-y-1">
                          <div className="font-semibold text-gray-900">
                            ₹{booking.amount || 0}
                          </div>
                          {booking.advancePaid > 0 && (
                            <div className="text-xs space-y-0.5 mt-0.5">
                              <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-800 border border-amber-200 inline-block mb-1">
                                ⚡ Advance Ride
                              </span>
                              <div className="text-gray-500 font-medium">
                                Paid: <span className="text-green-600 font-bold">₹{booking.advancePaid}</span>
                              </div>
                              <div className="text-gray-500 font-medium">
                                Due: <span className="text-orange-600 font-bold">₹{booking.remainingAmount || (booking.amount - booking.advancePaid)}</span>
                              </div>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            {booking.paymentMode && (
                              <span className={`text-sm px-2 py-1 rounded font-bold ${
                                booking.paymentMode === "COMPANY"
                                  ? "bg-indigo-100 text-indigo-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}>
                                {booking.paymentMode === "COMPANY" ? "🏢 Company" : "👤 Personal"}
                              </span>
                            )}
                            {booking.paymentMethod && (
                              <span className={`text-sm px-2 py-1 rounded font-bold ${getPaymentMethodBadgeClass(booking)}`}>
                                {getPaymentMethodDisplay(booking)}
                              </span>
                            )}
                          </div>
                          {booking.updatedAt && (
                            <span className="text-xs text-gray-400">
                              Updated{" "}
                              {booking.updatedAt
                                ? new Date(
                                    booking.updatedAt,
                                  ).toLocaleTimeString()
                                : "N/A"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{verticalAlign: 'top', overflow: 'hidden'}}>
                          <div className="text-center">
                            <span className={`inline-block px-3 py-1.5 rounded text-sm font-bold ${
                              booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                              booking.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                              booking.status === 'searching_driver' ? 'bg-indigo-100 text-indigo-800 animate-pulse' :
                              booking.status === 'driver_not_found' ? 'bg-rose-100 text-rose-800' :
                              (booking.status === 'pending' || booking.status === 'approved_by_corporate') ? 'bg-yellow-100 text-yellow-800' :
                              booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {booking.status === 'searching_driver' ? '🔍 Searching...' :
                               booking.status === 'driver_not_found' ? '❌ No Driver' :
                               booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                              {booking.status === 'completed' && <FaStar className="inline ml-1 h-3.5 w-3.5 text-yellow-500" title="Ratings available" />}
                              {booking.driver && (booking.status === 'confirmed' || booking.status === 'assigned') && (
                                <FaUserTie className="inline ml-1 h-3.5 w-3.5 text-blue-600" title="Driver assigned" />
                              )}
                            </span>
                            {booking.status === 'cancelled' && booking.cancellationReason && (
                              <div
                                className="text-xs text-red-600 font-semibold mt-1 bg-red-50 border border-red-100 rounded px-2 py-1 max-w-[180px] text-left mx-auto overflow-hidden"
                                style={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word'}}
                                title={booking.cancellationReason}
                              >
                                <span className="font-bold">Reason:</span> {booking.cancellationReason}
                              </div>
                            )}
                          </div>

                          {/* Status Change Dropdown */}
                          <div className="relative mt-2">
                            <select
                              className={`admin-dropdown w-full text-sm py-2 px-3 min-h-[36px] ${
                                updatingBookings.has(booking._id) ||
                                booking.status === "completed" ||
                                booking.status === "cancelled"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              style={{fontSize: '13px', lineHeight: '1.3'}}
                              value=""
                              disabled={
                                updatingBookings.has(booking._id) ||
                                booking.status === "completed" ||
                                booking.status === "cancelled"
                              }
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleStatusChange(booking._id, e.target.value);
                                  e.target.value = ""; // Reset dropdown
                                }
                              }}
                              title={
                                booking.status === "completed" ||
                                booking.status === "cancelled"
                                  ? `Cannot modify ${booking.status} booking status`
                                  : "Change booking status"
                              }
                            >
                              <option value="">
                                {booking.status === "completed" || booking.status === "cancelled"
                                  ? "Status Locked"
                                  : "Change to..."}
                              </option>
                              {/* Only allow pending → confirmed, not confirmed → pending */}
                              {(booking.status === 'pending' || booking.status === 'approved_by_corporate') && (
                                <option value="confirmed">✓ Mark as Confirmed</option>
                              )}
                              {/* Allow confirmed/assigned/inProgress → completed */}
                              {(booking.status === 'confirmed' || booking.status === 'assigned' || booking.status === 'inProgress') && (
                                <option value="completed">✓ Mark as Completed</option>
                              )}
                              {/* Allow cancellation from pending, confirmed, or assigned (but not completed) */}
                              {(booking.status === 'pending' || booking.status === 'approved_by_corporate' || booking.status === 'confirmed' || booking.status === 'assigned') && (
                                <option value="cancelled">✗ Cancel Booking</option>
                              )}
                            </select>

                            {updatingBookings.has(booking._id) && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-md">
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-500"></div>
                              </div>
                            )}
                          </div>
                      </td>
                      <td>
                        <div className="space-y-2">
                          {booking.driver ? (
                            <>
                              <div className="flex items-center text-sm">
                                <FaUserTie className="mr-2 text-blue-600 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-gray-900">
                                    {((booking.driver.user && booking.driver.user.name) || booking.driver.name || "Unknown")}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {((booking.driver.user && booking.driver.user.phone) || booking.driver.phone || "")}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center text-sm">
                                <FaCar className="mr-2 text-purple-600 flex-shrink-0" />
                                <span className="text-purple-700 font-medium text-xs">
                                  {booking.cabType?.name || booking.cabType || booking.cabName || "Standard Cab"}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center text-sm">
                                <FaCar className="mr-2 text-purple-600 flex-shrink-0" />
                                <span className="text-purple-700 font-medium text-xs">
                                  {booking.cabType?.name || booking.cabType || booking.cabName || "Standard Cab"}
                                </span>
                              </div>
                              <div className="text-gray-500 text-xs italic">
                                No driver assigned
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex flex-col items-end gap-2">
                          {booking.status === "completed" && (
                            <button
                              onClick={() => viewRatings(booking)}
                              className="bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1.5 rounded-md text-sm font-bold transition w-full flex items-center justify-center gap-1 border border-blue-100"
                            >
                              <FaStar className="w-3.5 h-3.5 flex-shrink-0" />
                              View Ratings
                            </button>
                          )}
                          
                          {(!booking.driver && (booking.status === "pending" || booking.status === "approved_by_corporate" || booking.status === "confirmed")) ? (
                            <button
                              onClick={() => {
                                setSelectedBooking(booking);
                                setShowDriverModal(true);
                              }}
                              className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-md text-sm font-bold transition shadow-sm w-full"
                            >
                              Assign Driver
                            </button>
                          ) : (booking.status === "assigned" || (booking.driver && booking.status === "confirmed")) ? (
                            <div className="flex flex-col items-end gap-1 w-full">
                              <button
                                onClick={() => removeDriver(booking._id)}
                                className="bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded-md text-sm font-bold transition border border-red-100 w-full"
                              >
                                Remove Driver
                              </button>
                              <div className="text-[11px] text-gray-500 font-medium">Awaiting confirmation</div>
                            </div>
                          ) : null}
                          
                          {(booking.status === "searching_driver") ? (
                            <div className="flex flex-col items-end gap-1 w-full">
                              <div className="bg-indigo-50 border border-indigo-100 text-indigo-700 px-3 py-1.5 rounded-md text-sm font-bold w-full flex items-center justify-center gap-2">
                                <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-indigo-500"></div>
                                Searching...
                              </div>
                              <button
                                onClick={() => handleRetryAutoAssign(booking._id)}
                                disabled={autoAssignLoading[booking._id]}
                                className="text-[11px] text-indigo-600 hover:underline font-bold mt-1"
                              >
                                {autoAssignLoading[booking._id] ? "Starting..." : "Retry Search"}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  setShowDriverModal(true);
                                }}
                                className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-md text-xs font-bold transition shadow-sm w-full mt-1.5"
                              >
                                Assign Driver / Vendor
                              </button>
                              <button
                                onClick={() => handleStopSearching(booking._id)}
                                disabled={autoAssignLoading[booking._id]}
                                className="bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded-md text-xs font-bold transition border border-red-100 w-full mt-1"
                              >
                                {autoAssignLoading[booking._id] ? "Stopping..." : "Stop Search / Cancel"}
                              </button>
                            </div>
                          ) : ((booking.status === "driver_not_found" || (booking.status === "confirmed" && !booking.driver))) ? (
                            <button
                              onClick={() => handleAutoAssign(booking._id)}
                              disabled={autoAssignLoading[booking._id]}
                              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md text-sm font-bold transition shadow-sm w-full flex items-center justify-center gap-2"
                            >
                              {autoAssignLoading[booking._id] ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
                              ) : (
                                <>
                                  <FaBolt className="w-3.5 h-3.5" />
                                  Auto Assign
                                </>
                              )}
                            </button>
                          ) : null}

                          <Link 
                            to={`/booking/${booking._id}`}
                            className="bg-gray-50 text-gray-700 hover:bg-gray-100 px-3 py-1.5 rounded-md text-sm font-bold transition border border-gray-200 w-full flex items-center justify-center gap-1"
                          >
                            <FaEye className="w-3.5 h-3.5" />
                            Details
                          </Link>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      className="py-8 text-center text-gray-500"
                      colSpan="10"
                    >
                      {searchTerm || statusFilter !== "all"
                        ? "No bookings matching the criteria"
                        : "No bookings found"}
                    </td>
                  </tr>
                )}
              </tbody>
              </table>
            </div>
          </div>
        )}
      </div>



      {/* Full Address & Add-on Services Detail Dialog Modal */}
      {routeDetailModalBooking && (
        <div 
          className="fixed inset-0 z-[99999] bg-black/60 backdrop-blur-sm flex justify-center items-start sm:items-center p-3 sm:p-5 overflow-y-auto"
          onClick={() => setRouteDetailModalBooking(null)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-xl w-full my-auto flex flex-col max-h-[90vh] border border-gray-100 animate-in fade-in zoom-in-95 duration-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-900 px-6 py-4 text-white flex items-center justify-between flex-shrink-0 shadow-sm border-b border-white/10">
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <FaMapMarkerAlt className="text-blue-200" />
                  Route & Service Details
                </h3>
                <p className="text-xs text-blue-100 font-mono mt-0.5">
                  Booking ID: #{routeDetailModalBooking.bookingId || routeDetailModalBooking._id}
                </p>
              </div>
              <button
                onClick={() => setRouteDetailModalBooking(null)}
                className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors cursor-pointer text-sm font-bold"
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1">
              {/* Pickup Address Box */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 shadow-2xs">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-3 h-3 rounded-full bg-blue-600 flex-shrink-0" />
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Pickup Location & Address
                  </span>
                </div>
                <p className="text-sm font-bold text-gray-900 pl-5">
                  {routeDetailModalBooking.from}
                </p>
                {routeDetailModalBooking.pickupAddress && 
                 routeDetailModalBooking.pickupAddress.trim().toLowerCase() !== routeDetailModalBooking.from?.trim().toLowerCase() && (
                  <div className="mt-2 pl-5 text-xs text-gray-700 bg-white/80 p-2.5 rounded-lg border border-blue-100 leading-relaxed">
                    <strong className="text-blue-950 block text-[11px] uppercase tracking-wider mb-0.5">Full Street Address:</strong>
                    {routeDetailModalBooking.pickupAddress}
                    {routeDetailModalBooking.pickupAddressDetails?.landmark && (
                      <span className="text-blue-800 font-medium block mt-1">
                        📍 Landmark: {routeDetailModalBooking.pickupAddressDetails.landmark}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Via Cities if present */}
              {routeDetailModalBooking.viaCities && routeDetailModalBooking.viaCities.length > 0 && (
                <div className="bg-amber-50/70 border border-amber-200/80 rounded-xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-3 h-3 rounded-full bg-amber-500 flex-shrink-0" />
                    <span className="text-xs font-bold text-amber-900 uppercase tracking-wider">
                      Via Cities (Multi-City Route)
                    </span>
                  </div>
                  <p className="text-xs text-amber-950 font-semibold pl-5">
                    {formatViaCitiesList(routeDetailModalBooking.viaCities)}
                  </p>
                </div>
              )}

              {/* Drop Address Box */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 shadow-2xs">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-3 h-3 rounded-full bg-emerald-600 flex-shrink-0" />
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Drop Location & Address
                  </span>
                </div>
                <p className="text-sm font-bold text-gray-900 pl-5">
                  {routeDetailModalBooking.to}
                </p>
                {routeDetailModalBooking.dropAddress && 
                 routeDetailModalBooking.dropAddress.trim().toLowerCase() !== routeDetailModalBooking.to?.trim().toLowerCase() && (
                  <div className="mt-2 pl-5 text-xs text-gray-700 bg-white/80 p-2.5 rounded-lg border border-emerald-100 leading-relaxed">
                    <strong className="text-emerald-950 block text-[11px] uppercase tracking-wider mb-0.5">Full Street Address:</strong>
                    {routeDetailModalBooking.dropAddress}
                    {routeDetailModalBooking.dropAddressDetails?.landmark && (
                      <span className="text-emerald-800 font-medium block mt-1">
                        📍 Landmark: {routeDetailModalBooking.dropAddressDetails.landmark}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* ADD ON SERVICES (OPTIONAL) SECTION */}
              <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-4 shadow-2xs">
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-base">✨</span>
                    <span className="text-xs font-bold text-amber-950 uppercase tracking-wider">
                      Add-on Services (Optional)
                    </span>
                  </div>
                  {routeDetailModalBooking.selectedAddOns && routeDetailModalBooking.selectedAddOns.length > 0 && (
                    <span className="text-xs font-bold bg-amber-200/80 text-amber-950 px-2.5 py-0.5 rounded-full border border-amber-300">
                      {routeDetailModalBooking.selectedAddOns.length} Added
                    </span>
                  )}
                </div>

                {routeDetailModalBooking.selectedAddOns && routeDetailModalBooking.selectedAddOns.length > 0 ? (
                  <div className="space-y-2">
                    {routeDetailModalBooking.selectedAddOns.map((addon, aIdx) => (
                      <div 
                        key={aIdx} 
                        className="bg-white p-2.5 rounded-lg border border-purple-100 flex items-center justify-between text-xs shadow-2xs"
                      >
                        <div>
                          <p className="font-bold text-purple-950">{addon.name || "Add-on Service"}</p>
                          {addon.description && (
                            <p className="text-[11px] text-gray-500 mt-0.5">{addon.description}</p>
                          )}
                        </div>
                        <span className="font-extrabold text-sm text-purple-900 bg-purple-50 px-2.5 py-1 rounded-md border border-purple-100 flex-shrink-0 ml-2">
                          +₹{addon.price || 0}
                        </span>
                      </div>
                    ))}
                    {routeDetailModalBooking.addOnsCost > 0 && (
                      <div className="flex justify-between items-center text-xs font-bold text-purple-950 pt-1 border-t border-purple-200/60 mt-2 px-1">
                        <span>Total Add-ons Amount:</span>
                        <span className="text-sm">₹{routeDetailModalBooking.addOnsCost}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 italic pl-1">
                    No optional add-on services were selected by customer for this ride.
                  </p>
                )}
              </div>

              {/* Ride & Customer Quick Summary */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-700 space-y-2 shadow-2xs">
                <div className="flex justify-between items-center py-0.5 border-b border-gray-200/60">
                  <span className="text-gray-500">Cab & Trip Type:</span>
                  <span className="font-bold text-gray-900">
                    {routeDetailModalBooking.cabType} • {routeDetailModalBooking.tripType === "roundTrip" ? "🔄 Round Trip" : routeDetailModalBooking.serviceType === "rental" ? "🚗 Rental" : routeDetailModalBooking.serviceType === "airport" ? "✈️ Airport" : "➡️ One Way"}
                  </span>
                </div>
                <div className="flex justify-between items-center py-0.5 border-b border-gray-200/60">
                  <span className="text-gray-500">Pickup Schedule:</span>
                  <span className="font-bold text-gray-900">
                    📅 {routeDetailModalBooking.date} at 🕒 {routeDetailModalBooking.pickupTime || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between items-center py-0.5 border-b border-gray-200/60">
                  <span className="text-gray-500">Customer:</span>
                  <span className="font-bold text-gray-900">
                    {routeDetailModalBooking.user?.name || "Guest"} ({routeDetailModalBooking.user?.phone || "No phone"})
                  </span>
                </div>
                <div className="flex justify-between items-center py-0.5 pt-1">
                  <span className="text-gray-500 font-semibold">Total Ride Fare:</span>
                  <span className="font-extrabold text-sm text-blue-700">
                    ₹{routeDetailModalBooking.amount || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-3.5 rounded-b-2xl border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                onClick={() => setRouteDetailModalBooking(null)}
                className="px-5 py-2 bg-gray-800 hover:bg-gray-900 text-white text-xs font-semibold rounded-lg transition-colors shadow-xs cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={totalFilteredBookings}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />
      {/* Driver Assignment Modal */}
      {showDriverModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">
                  Assign Driver to Booking
                </h3>
                <button
                  onClick={closeDriverAssignModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4 bg-blue-50 p-3 rounded-lg">
                <h4 className="font-medium mb-2">Booking Details</h4>
                <div className="grid grid-cols-1 gap-3 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-gray-600">Booking ID:</span>{" "}
                      {selectedBooking?.bookingId || selectedBooking?._id}
                    </div>
                    <div>
                      <span className="text-gray-600">Customer:</span>{" "}
                      {selectedBooking?.user.name}
                    </div>
                  </div>

                  <div className="border-t pt-2">
                    <div className="mb-2">
                      <span className="text-gray-600 font-medium">Route:</span>
                      <div className="text-blue-600 font-medium">
                        {selectedBooking?.pickupLocation?.name ||
                          selectedBooking?.from ||
                          "Not specified"}
                        {selectedBooking?.pickupLocation?.state &&
                          `, ${selectedBooking.pickupLocation.state}`}
                        {selectedBooking?.pickupAddressDetails?.pincode &&
                          ` ${selectedBooking.pickupAddressDetails.pincode}`}
                        <span className="mx-2 text-gray-400">→</span>
                        {selectedBooking?.dropLocation?.name ||
                          selectedBooking?.to ||
                          "Not specified"}
                        {selectedBooking?.dropLocation?.state &&
                          `, ${selectedBooking.dropLocation.state}`}
                        {selectedBooking?.dropAddressDetails?.pincode &&
                          ` ${selectedBooking.dropAddressDetails.pincode}`}
                      </div>
                    </div>

                    {(() => {
                      // Only show pickup address if different from location
                      const pickupLocationName =
                        selectedBooking?.pickupLocation?.name ||
                        selectedBooking?.from;
                      const pickupAddr = selectedBooking?.pickupAddress;
                      const pickupLandmark =
                        selectedBooking?.pickupAddressDetails?.landmark;

                      return pickupAddr &&
                        (pickupAddr !== pickupLocationName ||
                          pickupLandmark) ? (
                        <div className="mb-1">
                          <span className="text-gray-600 font-medium">
                            Pickup Address:
                          </span>
                          <div className="text-gray-700">
                            {pickupAddr}
                            {pickupLandmark && `, Near ${pickupLandmark}`}
                          </div>
                        </div>
                      ) : null;
                    })()}

                    {(() => {
                      // Only show drop address if different from location
                      const dropLocationName =
                        selectedBooking?.dropLocation?.name ||
                        selectedBooking?.to;
                      const dropAddr = selectedBooking?.dropAddress;
                      const dropLandmark =
                        selectedBooking?.dropAddressDetails?.landmark;

                      return dropAddr &&
                        (dropAddr !== dropLocationName || dropLandmark) ? (
                        <div className="mb-1">
                          <span className="text-gray-600 font-medium">
                            Drop Address:
                          </span>
                          <div className="text-gray-700">
                            {dropAddr}
                            {dropLandmark && `, Near ${dropLandmark}`}
                          </div>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  <div className="grid grid-cols-2 gap-2 border-t pt-2">
                    <div>
                      <span className="text-gray-600">Date:</span>{" "}
                      {selectedBooking?.date}
                    </div>
                    <div>
                      <span className="text-gray-600">Cab Type:</span>{" "}
                      {selectedBooking?.cabType?.name ||
                        selectedBooking?.cabType ||
                        selectedBooking?.cabName ||
                        "Standard Cab"}
                    </div>
                    <div>
                      <span className="text-gray-600">Amount:</span> ₹
                      {selectedBooking?.amount || selectedBooking?.totalAmount || 0}
                    </div>
                    <div>
                      <span className="text-gray-600">Payment:</span>{" "}
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getPaymentMethodBadgeClass(selectedBooking)}`}
                      >
                        {getPaymentMethodDisplay(selectedBooking)}
                      </span>
                    </div>
                    {selectedBooking?.advancePaid > 0 && (
                      <>
                        <div>
                          <span className="text-gray-600">Advance Paid:</span>{" "}
                          <span className="font-semibold text-green-600">₹{selectedBooking.advancePaid}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Remaining Due:</span>{" "}
                          <span className="font-semibold text-orange-600">₹{selectedBooking.remainingAmount || (selectedBooking.amount - selectedBooking.advancePaid)}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                            ⚡ Advance Payment Ride
                          </span>
                        </div>
                      </>
                    )}
                    {selectedBooking?.paymentId && selectedBooking?.paymentMethod === "ONLINE" && (
                      <div className="mt-2">
                        <span className="text-gray-600">Payment ID:</span>{" "}
                        <span className="text-sm font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                          {selectedBooking.paymentId}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Trip Type Information */}
                  <div className="border-t pt-3 mt-3">
                    <div className="mb-2">
                      <span className="text-gray-600 font-medium">Trip Type:</span>
                      <div className={`inline-block ml-2 px-3 py-1 rounded text-sm font-medium ${
                        selectedBooking?.tripType === "roundTrip"
                          ? "bg-purple-100 text-purple-800"
                          : selectedBooking?.tripType === "multiCity" || (selectedBooking?.viaCities && selectedBooking?.viaCities.length > 0)
                          ? "bg-green-100 text-green-800"
                          : "bg-blue-100 text-blue-800"
                      }`}>
                        {(() => {
                          if (selectedBooking?.tripType === "roundTrip") {
                            return "🔄 Round Trip";
                          } else if (selectedBooking?.tripType === "multiCity" || (selectedBooking?.viaCities && selectedBooking?.viaCities.length > 0)) {
                            return "🌐 Multi-City";
                          } else {
                            return "➡️ One Way";
                          }
                        })()}
                      </div>
                    </div>
 
                    {/* Return Date Information for Round Trips */}
                    {selectedBooking?.tripType === "roundTrip" && (selectedBooking?.returnDate || selectedBooking?.returnTime) && (
                      <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded">
                        <span className="text-purple-800 font-medium text-sm">Return Journey:</span>
                        <div className="text-purple-700 text-sm mt-1">
                          {selectedBooking.returnDate && `Date: ${formatReturnDate(selectedBooking.returnDate)}`}
                          {selectedBooking.returnTime && ` at ${selectedBooking.returnTime}`}
                        </div>
                      </div>
                    )}
 
                    {/* Via Cities Information for Multi-City */}
                    {(selectedBooking?.tripType === "multiCity" || (selectedBooking?.viaCities && selectedBooking?.viaCities.length > 0)) && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                        <span className="text-green-800 font-medium text-sm">Via Cities:</span>
                        <div className="text-green-700 text-sm mt-1">
                          {selectedBooking?.viaCities && selectedBooking.viaCities.length > 0
                            ? formatViaCitiesList(selectedBooking.viaCities)
                            : "Multi-city route details"}
                        </div>
                      </div>
                    )}
                  </div>
                  {selectedBooking?.paymentMethod === "ONLINE" && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center text-blue-800">
                        <FaWallet className="w-4 h-4 mr-2" />
                        <span className="font-medium text-sm">
                          Commission Check Required
                        </span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        For COD bookings, driver wallet balance will be checked
                        to ensure commission can be deducted on completion.
                      </p>
                    </div>
                  )}

                  {/* Package Details Section */}
                  {selectedBooking?.serviceType === "rental" && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center text-green-800 mb-2">
                        <FaClock className="w-4 h-4 mr-2" />
                        <span className="font-medium text-sm">Rental Package Details</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="text-gray-600 font-medium">Package:</span>
                          <div className="text-green-700 font-semibold">
                            📦 {selectedBooking?.packageName ||
                                `${selectedBooking?.rentalHours || 0} Hours / ${selectedBooking?.rentalKm || 0} KM`}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600 font-medium">Service City:</span>
                          <div className="text-gray-800">
                            {selectedBooking?.pickupLocation?.name ||
                             selectedBooking?.from || "Not specified"}
                          </div>
                        </div>
                        {/* Only show individual duration/distance if packageName doesn't already contain this info */}
                        {selectedBooking?.rentalHours && !selectedBooking?.packageName && (
                          <div>
                            <span className="text-gray-600 font-medium">Duration:</span>
                            <div className="text-gray-800">
                              🕒 {selectedBooking.rentalHours} Hours
                            </div>
                          </div>
                        )}
                        {selectedBooking?.rentalKm && !selectedBooking?.packageName && (
                          <div>
                            <span className="text-gray-600 font-medium">Distance Included:</span>
                            <div className="text-gray-800">
                              🛣️ {selectedBooking.rentalKm} KM
                            </div>
                          </div>
                        )}
                        {selectedBooking?.extraFarePerHour && (
                          <div className="col-span-2">
                            <span className="text-gray-600 font-medium">Extra Fare Rate:</span>
                            <div className="text-orange-600 font-semibold">
                              💰 ₹{selectedBooking.extraFarePerHour}/Hour
                              <span className="text-xs text-gray-500 ml-1">
                                (for hours beyond package limit)
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-700">
                        <strong>Note:</strong> This is a rental service booking. Driver should wait at pickup location during the rental period.
                      </div>
                    </div>
                  )}

                  {/* Airport Transfer Details */}
                  {selectedBooking?.serviceType === "airport" && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center text-blue-800 mb-2">
                        <FaCar className="w-4 h-4 mr-2" />
                        <span className="font-medium text-sm">Airport Transfer Details</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {selectedBooking?.airportCode && (
                          <div>
                            <span className="text-gray-600 font-medium">Airport:</span>
                            <div className="text-blue-700 font-semibold">
                              ✈️ {selectedBooking.airportCode}
                            </div>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600 font-medium">Transfer Type:</span>
                          <div className="text-gray-800">
                            {selectedBooking?.journeyType === "airport_pickup" ? "🛬 Airport Pickup" :
                             selectedBooking?.journeyType === "airport_drop" ? "🛫 Airport Drop" :
                             "🔄 Airport Transfer"}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 p-2 bg-blue-100 rounded text-xs text-blue-700">
                        <strong>Note:</strong> Airport transfer service. Please ensure timely pickup/drop as per flight schedule.
                      </div>
                    </div>
                  )}

                  {/* Outstation Trip Details */}
                  {(selectedBooking?.serviceType === "outstation" || !selectedBooking?.serviceType) && (
                    <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center text-purple-800 mb-2">
                        <FaRoute className="w-4 h-4 mr-2" />
                        <span className="font-medium text-sm">Outstation Trip Details</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {selectedBooking?.distance && (
                          <div>
                            <span className="text-gray-600 font-medium">Distance:</span>
                            <div className="text-purple-700 font-semibold">
                              ��️ {Math.round(selectedBooking.distance)} KM
                            </div>
                          </div>
                        )}
                        {selectedBooking?.duration && (
                          <div>
                            <span className="text-gray-600 font-medium">Duration:</span>
                            <div className="text-purple-700 font-semibold">
                              ⏱️ {selectedBooking.duration}
                            </div>
                          </div>
                        )}
                        {selectedBooking?.journeyType && (
                          <div className="col-span-2">
                            <span className="text-gray-600 font-medium">Journey Type:</span>
                            <div className="text-gray-800">
                              {selectedBooking.journeyType === "round_trip" ? "���� Round Trip" :
                               selectedBooking.journeyType === "one_way" ? "➡️ One Way" :
                               selectedBooking.journeyType}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 p-2 bg-purple-100 rounded text-xs text-purple-700">
                        <strong>Note:</strong> Outstation trip. Driver allowances and toll charges may apply.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Assignment Type Tabs */}
              <div className="flex border-b mb-4">
                <button
                  onClick={() => {
                    console.log('Switching to driver tab');
                    setAssignmentType("driver");
                  }}
                  className={`px-4 py-2 font-medium text-sm ${
                    assignmentType === "driver"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Assign Driver
                </button>
                <button
                  onClick={() => {
                    console.log('Switching to vendor tab');
                    console.log('Vendors available:', vendors.length);
                    setAssignmentType("vendor");
                    // Force refresh vendors when switching to vendor tab
                    if (vendors.length === 0) {
                      fetchVendorsManually();
                    }
                  }}
                  className={`px-4 py-2 font-medium text-sm ${
                    assignmentType === "vendor"
                      ? "border-b-2 border-blue-500 text-blue-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Assign Vendor
                </button>
              </div>

              {assignmentType === "driver" && (
                <>
                  <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <h4 className="font-medium text-blue-800">
                      �� Admin Drivers for{" "}
                      {selectedBooking?.cabType?.name ||
                        selectedBooking?.cabType ||
                        selectedBooking?.cabName ||
                        "Standard Cab"}
                    </h4>
                    <p className="text-xs text-blue-600 mt-1">
                      Direct assignment to admin drivers
                    </p>
                  </div>
                </>
              )}

              {assignmentType === "vendor" && (
                <>
                  <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded">
                    <h4 className="font-medium text-green-800">
                      🏢 Available Vendors
                    </h4>
                    <p className="text-xs text-green-600 mt-1">
                      Assign this booking to a vendor. The vendor will then assign one of their drivers through their dashboard.
                    </p>
                  </div>
                </>
              )}
              <div className="overflow-y-auto max-h-80">
                {/* Driver Assignment Content - Only show when driver tab is selected */}
                {assignmentType === "driver" && (
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Driver & Contact
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Status & Wallet
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Car Type
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Action
                        </th>
                      </tr>
                    </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* Filter drivers by availability and matching cab type */}
                    {drivers
                      .filter((driver) => {
                        // Only show available drivers
                        if (driver.status !== "available") return false;

                        // Always allow a match in this case regardless of cab type
                        // This ensures drivers show up for the assignment demo purpose

                        // For debugging, log the comparison
                        const bookingCabType = (
                          selectedBooking?.cabType?.name ||
                          selectedBooking?.cabType ||
                          selectedBooking?.cabName ||
                          ""
                        )
                          .toLowerCase()
                          .trim();
                        const driverCabType = (driver.carType || "")
                          .toLowerCase()
                          .trim();

                        console.log(
                          `Comparing booking cab type: "${bookingCabType}" with driver cab type: "${driverCabType}"`,
                        );

                        // Use vehicle type ID to match with booking cab type
                        // This allows us to match by ID instead of hardcoded strings
                        const vehicleTypeMatch =
                          driver.vehicleTypeId &&
                          cabTypes[driver.vehicleTypeId] &&
                          cabTypes[driver.vehicleTypeId]
                            .toLowerCase()
                            .trim() === bookingCabType;

                        // Check if cab types match exactly by name
                        const exactMatch = bookingCabType === driverCabType;

                        // Log when we find a match
                        if (exactMatch || vehicleTypeMatch) {
                          console.log(
                            `\u2705 MATCH FOUND: ${driver.name}'s cab type "${driverCabType}" matches booking cab type "${bookingCabType}"`,
                          );
                        }

                        return exactMatch || vehicleTypeMatch;
                      })
                      .map((driver) => (
                        <tr key={driver.id} className="hover:bg-gray-50">
                          <td className="py-3 px-3">
                            <div className="flex items-center">
                              <FaUser className="text-gray-500 mr-2" />
                              <div className="font-medium">{driver.name} {driver.phone}</div>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <div className="text-sm text-green-600 font-medium">Available</div>
                            {/* Always show wallet status */}
                            <WalletStatus
                              driverId={driver._id || driver.id}
                              bookingAmount={getBookingAmount(selectedBooking)}
                              user={user}
                              API_URL={API_URL}
                              paymentMethod={selectedBooking.paymentMethod}
                              cachedWalletData={driverWalletMap[driver._id || driver.id]}
                            />
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center">
                              <FaCar className="text-purple-500 mr-2" />
                              <div className="font-medium text-purple-700">
                                {driver.vehicleType
                                  ? typeof driver.vehicleType === "object"
                                    ? `${driver.vehicleType.name} ${driver.vehicleType.acAvailable ? "(AC)" : "(Non-AC)"}`
                                    : driver.vehicleType
                                  : driver.carType || "Unknown Type"}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <AssignButton
                              driver={driver}
                              selectedBooking={selectedBooking}
                              assignDriver={assignDriver}
                              user={user}
                              API_URL={API_URL}
                              cachedWalletData={driverWalletMap[driver._id || driver.id]}
                            />
                          </td>
                        </tr>
                      ))}
                    {drivers.filter((driver) => {
                      if (driver.status !== "available") return false;

                      // Use the same matching logic as above
                      const bookingCabType = (
                        selectedBooking?.cabType?.name ||
                        selectedBooking?.cabType ||
                        selectedBooking?.cabName ||
                        ""
                      )
                        .toLowerCase()
                        .trim();
                      const driverCabType = (driver.carType || "")
                        .toLowerCase()
                        .trim();

                      // Match based on vehicle type ID mapping from database
                      const vehicleTypeMatch =
                        driver.vehicleTypeId &&
                        cabTypes[driver.vehicleTypeId] &&
                        cabTypes[driver.vehicleTypeId].toLowerCase().trim() ===
                          bookingCabType;

                      // Exact match by name
                      const exactMatch = bookingCabType === driverCabType;

                      return exactMatch || vehicleTypeMatch;
                    }).length === 0 && (
                      <tr>
                        <td colSpan="4" className="py-6 text-center">
                          <div className="flex flex-col items-center">
                            <FaCar className="text-gray-400 text-3xl mb-2" />
                            <p className="text-gray-500 font-medium">
                              No available admin drivers for{" "}
                              {selectedBooking?.cabType?.name ||
                                selectedBooking?.cabType ||
                                selectedBooking?.cabName ||
                                "Standard Cab"}{" "}
                              cab type
                            </p>
                            <p className="text-gray-400 text-sm mt-1">
                              All admin drivers are assigned, or only vendor drivers are available.
                              Try the "Assign Vendor" tab.
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                    </tbody>
                  </table>
                )}

                {/* Vendor Assignment Content - Only show when vendor tab is selected */}
                {assignmentType === "vendor" && (
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Vendor & Contact
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Status & Details
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Drivers & Wallet
                        </th>
                        <th className="py-2 px-3 text-left text-sm font-medium">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {vendors.length === 0 && (
                        <tr>
                          <td colSpan="4" className="py-6 text-center">
                            <div className="flex flex-col items-center">
                              <FaBuilding className="text-gray-400 text-3xl mb-2" />
                              <div className="text-gray-500">
                                Loading vendors... ({vendors.length} vendors found)
                              </div>
                              <button
                                onClick={fetchVendorsManually}
                                className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                              >
                                Retry Loading Vendors
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {vendors
                        .filter((vendor) => vendor.verificationStatus === "approved")
                        .map((vendor) => (
                          <tr key={vendor._id} className="hover:bg-gray-50">
                            <td className="py-3 px-3">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                  <FaBuilding className="text-blue-600 w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {vendor.fullName || vendor.name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {vendor.email}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {vendor.phone}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <div className="text-sm">
                                <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                  vendor.verificationStatus === "approved"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}>
                                  {vendor.verificationStatus}
                                </div>
                                {vendor.businessName && (
                                  <div className="text-gray-600 mt-1">
                                    {vendor.businessName}
                                  </div>
                                )}
                                {vendor.city && (
                                  <div className="text-gray-500 text-xs mt-1">
                                    📍 {vendor.city}, {vendor.state}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-3">
                              <div className="text-sm text-gray-900">
                                {vendor.walletInfo?.totalDrivers || vendor.totalDrivers || 0} drivers
                              </div>
                              <div className="text-xs text-gray-500">
                                {vendor.walletInfo?.activeDrivers || 0} active
                              </div>
                              {vendor.walletInfo && (
                                <div className="mt-1 space-y-0.5">
                                  <div className="text-xs font-medium text-green-600">
                                    Total Wallet Balance: {formatCurrency(Number(vendorWalletMap[vendor._id] ?? 0))}
                                  </div>
                                  <div className="text-[11px] text-gray-500">
                                    Across {vendor.walletInfo.totalDrivers || 0} drivers
                                  </div>
                                  <div className="text-xs text-purple-600">
                                    {(() => {
                                      const rate = vendorCommissionMap[vendor._id]?.effectiveRate ??
                                                   vendor.commissionData?.commissionSettings?.effectiveRate ??
                                                   vendor.commissionData?.commissionStructure?.vendorRate ?? null;
                                      return rate != null ? `Commission Rate: ${rate}% per ride` : 'Commission Rate: N/A';
                                    })()}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-3">
                              <button
                                onClick={() => assignVendor(vendor._id)}
                                className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700 transition-colors"
                              >
                                Assign Vendor
                              </button>
                            </td>
                          </tr>
                        ))}
                      {vendors.filter((vendor) => vendor.verificationStatus === "approved").length === 0 && (
                        <tr>
                          <td colSpan="4" className="py-6 text-center">
                            <div className="flex flex-col items-center">
                              <FaBuilding className="text-gray-400 text-3xl mb-2" />
                              <p className="text-gray-500 font-medium">
                                No approved vendors available
                              </p>
                              <p className="text-gray-400 text-sm mt-1">
                                All vendors are either pending approval or assigned
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={closeDriverAssignModal}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Confirmation Modal */}
      <StatusConfirmationModal
        isOpen={showStatusModal}
        onClose={closeStatusModal}
        onConfirm={updateBookingStatus}
        booking={selectedBooking}
        newStatus={pendingStatusChange?.newStatus}
        isDriverControlled={
          selectedBooking &&
          (selectedBooking.status === "completed" ||
            selectedBooking.status === "cancelled")
        }
      />

      {/* Rating Viewer Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Booking Ratings</h3>
                <button
                  onClick={() => setShowRatingModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <FaTimes className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4 overflow-y-auto max-h-[70vh]">
              {selectedRatingData?.loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                  <span className="ml-3 text-gray-600">Loading ratings...</span>
                </div>
              ) : selectedRatingData?.error ? (
                <div className="text-center py-8">
                  <div className="text-red-500 mb-2">{selectedRatingData.error}</div>
                  <button
                    onClick={() => viewRatings(selectedRatingData.booking)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Booking Info - ALWAYS SHOWN */}
                  {selectedRatingData?.booking && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-slate-800">Booking Details</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500 font-medium">Booking ID:</span>
                          <div className="font-mono font-bold text-blue-600">
                            {selectedRatingData.booking.bookingId || selectedRatingData.booking._id}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium">Customer:</span>
                          <div className="font-bold text-slate-700">
                            {selectedRatingData.booking.user?.name || "Guest User"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedRatingData?.ratings ? (
                    <div className="space-y-6">
                      {/* Customer Rating */}
                      {selectedRatingData.ratings.customerRating && (
                        <div className="border border-gray-200 p-4 rounded-lg">
                          <h5 className="font-medium mb-3 flex items-center">
                            <FaUser className="mr-2 text-blue-600" />
                            Customer Rating for Driver
                          </h5>
                          <div className="flex items-center mb-2">
                            <div className="flex items-center mr-3">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar
                                  key={star}
                                  className={`h-5 w-5 ${
                                    star <= selectedRatingData.ratings.customerRating.rating
                                      ? "text-yellow-400"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-lg font-semibold">
                              {selectedRatingData.ratings.customerRating.rating}/5
                            </span>
                          </div>
                          {selectedRatingData.ratings.customerRating.comment && (
                            <div className="mt-3">
                              <span className="text-gray-600 text-sm">Comment:</span>
                              <p className="mt-1 text-gray-800 italic">
                                "{selectedRatingData.ratings.customerRating.comment}"
                              </p>
                            </div>
                          )}
                          <div className="mt-2 text-xs text-gray-500">
                            Rated on: {new Date(selectedRatingData.ratings.customerRating.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      )}

                      {/* Driver Rating */}
                      {selectedRatingData.ratings.driverRating && (
                        <div className="border border-gray-200 p-4 rounded-lg">
                          <h5 className="font-medium mb-3 flex items-center">
                            <FaUserTie className="mr-2 text-green-600" />
                            Driver Rating for Customer
                          </h5>
                          <div className="flex items-center mb-2">
                            <div className="flex items-center mr-3">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <FaStar
                                  key={star}
                                  className={`h-5 w-5 ${
                                    star <= selectedRatingData.ratings.driverRating.rating
                                      ? "text-yellow-400"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-lg font-semibold">
                              {selectedRatingData.ratings.driverRating.rating}/5
                            </span>
                          </div>
                          {selectedRatingData.ratings.driverRating.comment && (
                            <div className="mt-3">
                              <span className="text-gray-600 text-sm">Comment:</span>
                              <p className="mt-1 text-gray-800 italic">
                                "{selectedRatingData.ratings.driverRating.comment}"
                              </p>
                            </div>
                          )}
                          <div className="mt-2 text-xs text-gray-500">
                            Rated on: {new Date(selectedRatingData.ratings.driverRating.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      )}

                      {!selectedRatingData.ratings.customerRating && !selectedRatingData.ratings.driverRating && (
                        <div className="text-center py-8">
                          <FaStar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                          <p className="text-gray-500">No ratings available for this booking yet.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FaStar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">
                        {selectedRatingData?.message || "No ratings available for this booking yet."}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowRatingModal(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
